import { Component, OnInit, OnDestroy, DestroyRef, HostListener, inject, ViewChildren, ViewChild, QueryList, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { FormArray, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Workflows } from 'src/providers/workflows/workflows';
import { AgentsProvider } from 'src/providers/agents/agents';
import { Client } from 'src/providers/client/client';
import { GuestReservations } from 'src/providers/guest-reservations/guest-reservations';
import { Addresses } from 'src/providers/customer/addresses';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { SETTINGS_NOT_SAVED_MESSAGE } from 'src/shared/util/settings-save-errors';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { AgentActivityPage } from './agent-activity/agent-activity';
import { ActivityRow, ActivitySummary, toActivityRows, toActivitySummary } from 'src/shared/components/agent-activity-list/activity-row';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { ChipTextArea, ChipPlaceholder } from 'src/shared/components/chip-text-area/chip-text-area';
import { ModalController } from '@ionic/angular';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { JobRequestWorkflows } from 'src/pages/job-request-workflows/job-request-workflows';
import { WorkflowRunPage } from 'src/pages/workflows/workflow-run/workflow-run';
import { DateTime as LuxonDateTime } from 'luxon';
import { Pros } from 'src/providers/pros/pros';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { MyProPage } from 'src/pages/my-pros/my-pro/my-pro';
import { TidyFindNewProPage } from 'src/pages/my-pros/tidy-find-new-pro/tidy-find-new-pro';
import { AddProPage } from 'src/pages/my-pros/add-pro/add-pro';
import { ConfirmPage } from 'src/pages/confirm/confirm.component';
import { InputComponent } from 'src/tidy-ui-components/components/form/input/input.component';
import { WORKFLOW_TAGS, getImpliedTagForTriggerCategory, getWorkflowTagsForClient } from 'src/shared/constants/workflowTags';
import { isHiddenAgentTrigger } from 'src/shared/constants/agentTriggers';
import { snakeCaseToTitleCase } from 'src/shared/util/snake-to-tile-case';

interface WorkflowConfig {
  id: string;
  name: string;
  instructions: string;
  blockActions: string[];
  inputValues: any;
  actionId?: string;
  actionKey?: string;
  actionName?: string;
  enabled?: boolean;
  tooltipLines?: {label: string, value: string}[];
}

interface TriggerItem {
  key: string;
  safeKey: string;
  title: string;
  agentKey: string;
  agentTitle: string;
  category?: string;
  enabled: boolean;
  instructions: string;
  blockActions: string[];
  inputValues: any;
  allowedActions: any[];
  sourceType: 'agent' | 'workflow';
  workflowTriggerId?: number;
  dynamicInputs?: any[];
  triggerOutputVariables?: any[];
  workflowIds?: number[];
  iconUrl?: string;
  description?: string;
}

@Component({
  selector: 'tidy-unified-agent',
  templateUrl: 'unified-agent.html',
  styleUrls: ['./unified-agent.scss'],
  standalone: false,
})
export class UnifiedAgentPage implements OnInit, OnDestroy {

  @ViewChildren(ChipTextArea) chipTextAreaComponents!: QueryList<ChipTextArea>;

  /**
   * Controls which section of the page renders. Used by mobile pages to
   * embed this component and show only one panel at a time.
   *  - null (default): full desktop 3-column layout
   *  - 'triggers': left rail only (triggers list)
   *  - 'detail':   center panel only (selected trigger config)
   *  - 'activity': activity feed only
   */
  @Input() mobileView: 'triggers' | 'detail' | 'activity' | null = null;

  /** When set, pre-select this trigger after data loads (used by mobile detail/activity pages). */
  @Input() triggerKey: string | null = null;

  /** Emitted when a trigger is clicked in the left rail. Mobile triggers page uses this to navigate. */
  @Output() triggerSelected = new EventEmitter<TriggerItem>();

  loaded = false;
  showLoadingSpinner = false;

  // First-load skeleton row counts; placeholder widths vary per row via
  // nth-child rules in the scss so the grey lines read like real text.
  readonly skTriggerRows = [1, 2, 3, 4, 5, 6, 7];
  readonly skWorkflowChips = [1, 2, 3];
  readonly skActionNodes = [1, 2];
  readonly skActivityRows = [1, 2, 3, 4, 5, 6];

  allWorkflows: any[] = [];
  workflowTriggerTypes: any[] = [];
  allTriggers: TriggerItem[] = [];
  enabledTriggers: TriggerItem[] = [];
  filteredTriggers: TriggerItem[] = [];
  filteredCustomTriggers: TriggerItem[] = [];
  availableTriggers: TriggerItem[] = [];
  selectedTrigger: TriggerItem | null = null;
  showAddTriggerDropdown = false;
  hasProperties = true;

  // Activity panel state
  activityRuns: any[] = [];
  activityRows: ActivityRow[] = [];
  activityLoading = false;
  activityLoadingMore = false;
  activityFilterWorkflowId: number | null = null;
  activityFilterName = '';
  activityAgentOptions: SelectModel[] = [];
  activitySummary: ActivitySummary | null = null;
  activityTotalRecords = 0;
  activityFailedRecords: number | null = null;
  private activityPage = 1;
  private activityRequestSeq = 0;
  private readonly activityPerPage = 50;
  activityStartDate: string;
  activityEndDate: string;
  activityDateRangeForm: UntypedFormGroup;
  activitySelectOptions = [
    { viewValue: 'Custom', value: 'custom' },
    { viewValue: 'Today', value: 'today' },
    { viewValue: 'Last Week', value: 'last_week' },
    { viewValue: 'Last Month', value: 'last_month' },
    { viewValue: 'All Time', value: 'all_time' }
  ];
  addTriggerSearchText = '';

  workflowConfigs: WorkflowConfig[] = [];
  visibleWorkflowConfigs: WorkflowConfig[] = [];
  showAllWorkflowChips = false;
  workflowChipLimit = 100;
  activeWorkflowConfigId: string | null = null;
  isNewWorkflowMode = false;
  @ViewChild('workflowChipsContainer') workflowChipsContainerRef: ElementRef;
  @ViewChild('workflowTitleInput') workflowTitleInput: InputComponent;

  workflowChipSearchForm: UntypedFormGroup;

  form: UntypedFormGroup;
  searchForm: UntypedFormGroup;
  workflowNameForm: UntypedFormGroup;
  workflowTagItems = getWorkflowTagsForClient(localStorage.getItem('isRentalClient') === 'true');
  private userEditedFirstTag = false;
  private impliedTag: string | null = null;
  private impliedSlotActive = false;

  blockedActionItems: any[] = [];

  allActions: any[] = [];
  allActionsData: any[] = [];
  filteredActions: any[] = [];
  selectedActions: any[] = [];
  actionFormIds: string[] = [];
  selectedChipTextAreaId: string = null;
  actionForms: UntypedFormGroup[] = [];
  actionSearchForm: UntypedFormGroup;
  isAutomationEnabled = true;

  // Auto-schedule state
  isRentalClient = localStorage.getItem('isRentalClient') === 'true';
  isAutoScheduleMode = false;
  autoScheduleAddresses: any[] = [];
  selectedAutoScheduleAddress: any = null;
  autoScheduleForm: UntypedFormGroup;
  autoScheduleJobControls: string[] = ['job0'];
  autoScheduleJobsCount = 0;
  autoScheduleJobItems: any[] = [];
  autoScheduleTimes: { start: any[], end: any[] };
  autoScheduleTimeRangeErrorMessage = '';
  autoScheduleNumberDaysPriorToCheckInItems: any[] = [];
  autoScheduleMaxDaysItems: any[] = [];
  autoScheduleLoading = false;
  autoScheduleAutomaticBooking = false;
  filteredAutoScheduleAddresses: any[] = [];
  showAllAutoScheduleChips = false;
  autoScheduleSearchForm: UntypedFormGroup;
  autoScheduleChipLimit = 100;
  visibleAutoScheduleAddresses: any[] = [];

  // Pro-search state
  isProSearchMode = false;
  proSearchAddresses: any[] = [];
  selectedProSearchAddress: any = null;
  proSearchRows: any[] = [];
  proSearchCurrentRows: any[] = [];
  proSearchHeaders = ['Properties', 'Service', 'Actions', 'last-column'];
  proSearchLoading = false;
  filteredProSearchAddresses: any[] = [];
  showAllProSearchChips = false;
  proSearchSearchForm: UntypedFormGroup;
  proSearchChipLimit = 100;
  visibleProSearchAddresses: any[] = [];
  proSearchCategoryItems: any[] = [];
  visibleProSearchCategoryItems: any[] = [];
  showAllProSearchCategoryChips = false;
  proSearchCategoryChipLimit = 100;
  isMeasuringProSearchCategoryChips = false;
  private _proSearchCategoryChipsContainerRef: ElementRef | null = null;

  @ViewChild('proSearchCategoryChipsContainer')
  set proSearchCategoryChipsContainerRef(ref: ElementRef | undefined) {
    this._proSearchCategoryChipsContainerRef = ref || null;
    if (ref && this.proSearchCategoryItems.length > 0 && !this.showAllProSearchCategoryChips) {
      this.measureProSearchCategoryChips();
    }
  }
  proSearchCategorySearchForm: UntypedFormGroup;
  proSearchCategoryForm: UntypedFormGroup;
  // Pro-search priority list state (inlined from job-request-workflows)
  proSearchProsData: any = null;
  private proSearchPrioritiesSave: Promise<unknown> = Promise.resolve();
  private proSearchRowsOutdated = false;
  proSearchPriorityListSentence = '';
  proSearchEditPrio = false;
  proSearchAllowsFindNewPros = false;
  proSearchIsAddressOutsideUS = false;
  proSearchHasPros = false;
  proSearchHasPrivatePro = false;
  proSearchServiceCategoryItems: any[] = [];
  proSearchTimeCutoffItems: any[] = [];
  proSearchPercentCutoffItems: any[] = [];
  proSearchClientSettings: any = null;
  proSearchSettingsForm: UntypedFormGroup;
  @ViewChild('proSearchChipsContainer') proSearchChipsContainerRef: ElementRef;
  isMeasuringProSearchChips = false;

  selectedPropertyChipId: number | null = null;
  isPropertyChipSelected = false;
  proSearchAddressSelectItems: any[] = [];
  proSearchAddressSelectForm: UntypedFormGroup;

  // Message agent state
  isMessageAgentMode = false;
  messageAgentSettings: any = null;
  // The open message agent's on/off; isAutomationEnabled is the workflow's.
  messageAgentEnabled = false;
  messageAgentInstructionsForm: UntypedFormGroup;
  // What the message-agent panel loaded from the server (normalized like the
  // save payload). performMessageAgentSave refuses to POST while the payload
  // still equals this — merely visiting a Guest/Pro Message Received trigger
  // must never create a settings row. That matters because the GET serves the
  // PLATFORM DEFAULTS when a client has no row of their own; a phantom save
  // would freeze those defaults (or a blank) into a real per-client override.
  private messageAgentLoadedSnapshot: { enabled: boolean; instructions: string; blocked_actions: string[] } | null = null;
  messageAgentActivity: any[] = [];
  messageAgentActivityLoading = false;
  messageAgentBlockedActionItems: any[] = [];
  private messageAgentFormSubscription: Subscription;
  private updateMessageAgentSubject = new Subject<void>();
  private messageAgentSaveSubscription: Subscription;
  // One set-user-settings POST at a time. The debounces space saves 600 ms
  // apart, but a slow save is still in flight when the next one starts: two
  // first saves at once each created a settings row server-side, after which
  // every save answered 500 (Sentry CLIENT-APP-5WJ), and the older text could
  // land after the newer. A save asked for meanwhile runs once, with the
  // latest form values, after the current one answers.
  private messageAgentSaveInFlight = false;
  private messageAgentSaveSending: any = null;
  // Saves waiting for the one in flight, each fixed to the agent and form values it was
  // asked for with. Read later, a queued save could go to the trigger the customer
  // switched to while the first one ran (a Pro text turning the Guest agent on).
  private messageAgentSaveQueue: { settings: any; agentKey: string; payload: { enabled: boolean; instructions: string; blocked_actions: string[] } }[] = [];

  isSaving = false;
  isPublishing = false;
  isLoadingTrigger = false;
  isViewingPublished = false;
  publishedVersionIsLatestVersion = true;
  publishedTriggerDisplay: any[] = [];
  publishedActionsDisplay: any[] = [];
  activeWorkflowData: any = null;
  private isFormInitializing = false;
  private isCreatingWorkflow = false;
  private isSavingWorkflowVersion = false;
  private isAutoScheduleFormInitializing = false;
  private activeWorkflowLatestVersionId: number | null = null;
  private latestVersion: any = null;
  private updateAgentSubject = new Subject<void>();
  private updateAgentSubscription: Subscription;
  private autoScheduleFormSubscription: Subscription;
  private updateAutoScheduleSubject = new Subject<void>();
  private autoScheduleSubscription: Subscription;
  private formSubscriptions: Subscription[] = [];
  dynamicInputsLoaded = false;
  radioItems: { [label: string]: any[] } = {};

  specialTriggerFields: { [key: string]: string[] } = {
    'scheduled.every_day': ['timezone', 'trigger_on_weekends', 'time_of_day'],
    'scheduled.every_hour': ['timezone', 'trigger_on_weekends'],
    'scheduled.every_week': ['timezone', 'time_of_day', 'day_of_the_week'],
    'scheduled.every_month': ['timezone', 'time_of_day', 'date_of_the_month'],
    'custom.job.should_have_started': ['past_from_start_value', 'past_from_start_unit'],
    'custom.job.should_have_completed': ['past_from_end_value', 'past_from_end_unit'],
    'custom.job.start_within': ['start_within_value', 'start_within_unit'],
    'custom.guest_reservation.checkin_within': ['checkin_within_value', 'checkin_within_unit'],
    'custom.issue_report.due_date_within_range': ['due_date_within_value', 'due_date_within_direction']
  };

  private allSpecialFieldNames = new Set<string>(
    Object.values(this.specialTriggerFields).flat()
  );

  timezoneItems: any[] = [
    { viewValue: 'UTC', value: 'UTC' },
    { viewValue: 'US/Eastern', value: 'US/Eastern' },
    { viewValue: 'US/Central', value: 'US/Central' },
    { viewValue: 'US/Mountain', value: 'US/Mountain' },
    { viewValue: 'US/Pacific', value: 'US/Pacific' },
    { viewValue: 'US/Alaska', value: 'US/Alaska' },
    { viewValue: 'US/Hawaii', value: 'US/Hawaii' },
    { viewValue: 'Europe/London', value: 'Europe/London' },
    { viewValue: 'Europe/Paris', value: 'Europe/Paris' },
    { viewValue: 'Europe/Berlin', value: 'Europe/Berlin' },
    { viewValue: 'Asia/Tokyo', value: 'Asia/Tokyo' },
    { viewValue: 'Asia/Shanghai', value: 'Asia/Shanghai' },
    { viewValue: 'Australia/Sydney', value: 'Australia/Sydney' }
  ];
  triggerOnWeekendsItems: any[] = [
    { viewValue: 'Yes', value: 'yes' },
    { viewValue: 'No', value: 'no' }
  ];
  timeOfDayItems: any[] = [];
  dayOfTheWeekItems: any[] = [
    { viewValue: 'Sunday', value: 'sunday' },
    { viewValue: 'Monday', value: 'monday' },
    { viewValue: 'Tuesday', value: 'tuesday' },
    { viewValue: 'Wednesday', value: 'wednesday' },
    { viewValue: 'Thursday', value: 'thursday' },
    { viewValue: 'Friday', value: 'friday' },
    { viewValue: 'Saturday', value: 'saturday' }
  ];
  dateOfTheMonthItems: any[] = [];
  timeUnitItems: any[] = [
    { viewValue: 'Minutes', value: 'minutes' },
    { viewValue: 'Hours', value: 'hours' },
    { viewValue: 'Days', value: 'days' }
  ];
  timeValueItems: any[] = [];
  dueDateDirectionItems: any[] = [
    { viewValue: 'Before', value: 'before' },
    { viewValue: 'After', value: 'after' }
  ];

  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  get showInlineActivity(): boolean {
    return this.windowService.windowSize > 1100;
  }

  get isLeftRailCollapsed(): boolean {
    return this.windowService.windowSize <= 900;
  }

  openActivityPanel() {
    if (this.isMessageAgentMode) {
      this.rightSidePanelService.openRightSidePanel(AgentActivityPage, {
        activityRuns: this.messageAgentActivity,
        workflowIds: [],
        activitySelectOptions: this.activitySelectOptions,
        isMessageAgent: true,
        agentKey: this.selectedTrigger?.agentKey
      });
    } else {
      this.rightSidePanelService.openRightSidePanel(AgentActivityPage, {
        activityRuns: this.activityRuns,
        workflowIds: this.allWorkflowIds(),
        agents: this.activityAgentOptions,
        filterWorkflowId: this.activityFilterWorkflowId,
        filterName: this.activityFilterName,
        totalRecords: this.activityTotalRecords,
        failedRecords: this.activityFailedRecords,
        page: this.activityPage,
        activitySelectOptions: this.activitySelectOptions
      });
    }
  }

  /**
   * Trigger-card click handler. On the mobile triggers list, emit for the
   * parent page to handle (it will navigate to the detail route). On desktop
   * or the mobile detail page, fall through to the normal selectTrigger flow.
   */
  onTriggerCardClick(trigger: TriggerItem) {
    if (this.mobileView === 'triggers') {
      this.triggerSelected.emit(trigger);
      return;
    }
    this.selectTrigger(trigger);
  }

  constructor(
    private fb: UntypedFormBuilder,
    private workflowsProvider: Workflows,
    private client: Client,
    public guestReservations: GuestReservations,
    private addressesProvider: Addresses,
    public windowService: WindowService,
    private util: Util,
    private rightSidePanelService: RightSidePanelService,
    private pros: Pros,
    private currentAddress: CurrentAddress,
    private modalCtrl: ModalController,
    private agentsProvider: AgentsProvider,
  ) {
    this.form = this.fb.group({
      instructions: [''],
      blockedActions: [[]]
    });
    this.searchForm = this.fb.group({
      search: ['']
    });
    this.workflowNameForm = this.fb.group({
      workflowName: [''],
      tags: this.fb.array([this.fb.control('')])
    });
    this.actionSearchForm = this.fb.group({
      search: ['']
    });
    this.autoScheduleSearchForm = this.fb.group({
      search: ['']
    });
    this.proSearchSearchForm = this.fb.group({
      search: ['']
    });
    this.workflowChipSearchForm = this.fb.group({
      search: ['']
    });
    this.proSearchCategorySearchForm = this.fb.group({
      search: ['']
    });
    this.proSearchCategoryForm = this.fb.group({
      category: ['']
    });
    this.proSearchAddressSelectForm = this.fb.group({
      address: ['']
    });
    this.proSearchSettingsForm = this.fb.group({
      timeCutoff: [''],
      percentCutoff: [''],
    });
    this.proSearchTimeCutoffItems = this.buildProSearchTimeCutoffItems();
    this.proSearchPercentCutoffItems = this.buildProSearchPercentCutoffItems();
    this.autoScheduleForm = this.fb.group({
      startTime: [''],
      endTime: [''],
      job0: ['', Validators.required],
      maxDays: [''],
      numberDaysPriorToCheckIn: ['']
    });
    this.timeOfDayItems = this.buildTimeOfDayItems();
    this.dateOfTheMonthItems = this.buildDateOfTheMonthItems();
    this.timeValueItems = this.buildTimeValueItems();
    this.autoScheduleNumberDaysPriorToCheckInItems = this.buildNumberDaysPriorToCheckInItems();
    this.autoScheduleMaxDaysItems = this.buildAutoScheduleMaxDaysItems();
    this.activityDateRangeForm = this.fb.group({
      start: '',
      end: '',
      customStringSelect: 'all_time'
    });
    this.messageAgentInstructionsForm = this.fb.group({
      instructions: [''],
      blockedActions: [[]]
    });
  }

  get tagsFormArray(): FormArray {
    return this.workflowNameForm.get('tags') as FormArray;
  }

  // The tags to PERSIST on the workflow. The implied slot (first select,
  // auto-filled from the trigger category) is display-only: it is excluded
  // unless the user changed it to something else, which makes it explicit.
  getTagsValue(): string[] {
    const rawValues: string[] = this.tagsFormArray.value.map((tag: string) => (tag || '').trim());
    const persisted = (this.impliedSlotActive && rawValues[0] === this.impliedTag)
      ? rawValues.slice(1)
      : rawValues;
    return persisted
      .filter((tag: string) => !!tag)
      .filter((tag: string, idx: number, arr: string[]) => arr.indexOf(tag) === idx);
  }

  // Preserves whatever non-user-facing (system) tags the record already has —
  // e.g. 'ai_agent' — so a category edit never drops them. Deliberately does
  // NOT infer system tags from the workflow name: that fabricated an 'ai_agent'
  // tag on records that never had one, and broke when an agent was renamed.
  private mergeWithSystemTags(
    userTags: string[],
    existingTags: string[] | undefined
  ): string[] {
    const userTagSet = new Set(WORKFLOW_TAGS.map(t => t.value));
    const systemTags = (existingTags || []).filter(t => !userTagSet.has(t));
    return [...systemTags, ...userTags];
  }

  addTag(): void {
    this.tagsFormArray.push(this.fb.control(''));
  }

  removeTag(index: number): void {
    if (index === 0 || this.tagsFormArray.length <= 1) {return;}
    this.tagsFormArray.removeAt(index);
  }

  onTagChange(index: number): void {
    if (index === 0 && this.impliedSlotActive && this.tagsFormArray.at(0)?.value !== this.impliedTag) {
      // The user overrode the implied slot — their choice becomes an explicit
      // (persisted) tag. The implied tag reappears first on next load.
      this.impliedSlotActive = false;
      this.userEditedFirstTag = true;
    } else if (index === 0) {
      this.userEditedFirstTag = true;
    }
  }

  /**
   * Rebuilds the category selects for a workflow: the tag implied by the
   * selected trigger's category first (display-only, never persisted), then
   * the workflow's explicit user-facing tags. Falls back to a single empty
   * select when the workflow has neither.
   */
  private patchTagsForm(tags: string[] | undefined | null): void {
    const userTagSet = new Set(WORKFLOW_TAGS.map(t => t.value));
    const explicitTags = (tags || []).filter(t => userTagSet.has(t));
    const implied = getImpliedTagForTriggerCategory(this.selectedTrigger?.category);
    // Only show the implied slot when the tag is selectable for this account
    // type — e.g. 'bills' isn't an option on residential accounts.
    this.impliedTag = (implied && this.workflowTagItems.some(item => item.value === implied)) ? implied : null;
    this.impliedSlotActive = !!this.impliedTag;
    const display = this.impliedTag
      ? [this.impliedTag, ...explicitTags.filter(t => t !== this.impliedTag)]
      : [...explicitTags];
    if (!display.length) {
      display.push('');
    }
    while (this.tagsFormArray.length > 0) {
      this.tagsFormArray.removeAt(0, { emitEvent: false });
    }
    display.forEach((tag) => {
      this.tagsFormArray.push(this.fb.control(tag), { emitEvent: false });
    });
    this.userEditedFirstTag = !this.impliedSlotActive && explicitTags.length > 0;
  }

  async ngOnInit() {
    // When a mobile user lands on /agent directly (no embedding mobileView set),
    // redirect to the dedicated mobile triggers list route.
    if (this.mobileView === null && !this.windowService.isDesktopRes) {
      this.router.navigate(['/agent/triggers'], { replaceUrl: true });
      return;
    }

    this.updateAgentSubscription = this.updateAgentSubject
      .pipe(debounceTime(50))
      .subscribe(() => {
        this.performUpdateAgent();
      });

    this.autoScheduleSubscription = this.updateAutoScheduleSubject
      .pipe(debounceTime(50))
      .subscribe(() => this.performAutoScheduleSave());

    this.messageAgentSaveSubscription = this.updateMessageAgentSubject
      .pipe(debounceTime(600))
      .subscribe(() => this.performMessageAgentSave());

    this.searchForm.get('search').valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        this.filterTriggers(value);
      });

    this.autoScheduleSearchForm.get('search').valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.filterAutoScheduleAddresses();
      });

    this.proSearchSearchForm.get('search').valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.filterProSearchAddresses();
      });

    this.workflowChipSearchForm.get('search').valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.filterWorkflowChips();
      });

    this.proSearchCategorySearchForm.get('search').valueChanges
      .pipe(debounceTime(300))
      .subscribe(() => {
        this.filterProSearchCategoryChips();
      });

    this.actionSearchForm.get('search').valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        this.searchActions(value);
      });

    this.handleRouterEvents();

    // One-shot deep-link keys (set by automations-preview-button) win over the
    // last-used state so "open this agent" clicks land on the right workflow.
    const deeplinkTriggerKey = localStorage.getItem('unified_agent_deeplink_trigger_key');
    const deeplinkWorkflowId = localStorage.getItem('unified_agent_deeplink_workflow_id');
    localStorage.removeItem('unified_agent_deeplink_trigger_key');
    localStorage.removeItem('unified_agent_deeplink_workflow_id');
    const savedState = {
      tab: null,
      // Prefer an explicit triggerKey input (mobile pages set this from the route),
      // then a pending deep link, then the last-used key stored in localStorage.
      triggerKey: this.triggerKey || deeplinkTriggerKey || localStorage.getItem('unified_agent_trigger_key'),
      workflowId: deeplinkWorkflowId || localStorage.getItem('unified_agent_workflow_id'),
      autoScheduleAddressId: localStorage.getItem('unified_agent_auto_schedule_address_id'),
      proSearchAddressId: localStorage.getItem('unified_agent_pro_search_address_id'),
    };

    await this.loadData();
    this.loaded = true;
    // Mobile triggers-list view never auto-selects a trigger — otherwise it would
    // immediately start loading workflow configs the user can't see.
    if (this.mobileView !== 'triggers' && this.hasProperties) {
      await this.restoreNavigationState(savedState);
    }
    if (this.mobileView !== 'triggers' && !this.selectedTrigger) {
      this.loadActivity();
    }
  }

  handleRouterEvents() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(async (event: NavigationEnd) => {
      if (!event.url.includes('/agent')) {
        this.updateAgentSubscription?.unsubscribe();
        this.updateAgentSubscription = null;
        this.autoScheduleSubscription?.unsubscribe();
        this.autoScheduleSubscription = null;
        this.messageAgentSaveSubscription?.unsubscribe();
        this.messageAgentSaveSubscription = null;
      } else if (this.loaded) {
        if (!this.updateAgentSubscription) {
          this.updateAgentSubscription = this.updateAgentSubject
            .pipe(debounceTime(50))
            .subscribe(() => this.performUpdateAgent());
        }
        if (!this.autoScheduleSubscription) {
          this.autoScheduleSubscription = this.updateAutoScheduleSubject
            .pipe(debounceTime(50))
            .subscribe(() => this.performAutoScheduleSave());
        }
        if (!this.messageAgentSaveSubscription) {
          this.messageAgentSaveSubscription = this.updateMessageAgentSubject
            .pipe(debounceTime(600))
            .subscribe(() => this.performMessageAgentSave());
        }
        await this.refreshDataInBackground();
        // The page instance survives in the nav stack, so ngOnInit (and its
        // deep-link restore) doesn't re-run on re-entry — apply any pending
        // deep link here, after the refresh re-selected the previous trigger.
        await this.applyPendingDeepLink();
      }
    });
  }

  private async applyPendingDeepLink(): Promise<void> {
    const triggerKey = localStorage.getItem('unified_agent_deeplink_trigger_key');
    const workflowId = localStorage.getItem('unified_agent_deeplink_workflow_id');
    if (!triggerKey && !workflowId) {return;}
    localStorage.removeItem('unified_agent_deeplink_trigger_key');
    localStorage.removeItem('unified_agent_deeplink_workflow_id');
    if (triggerKey && this.selectedTrigger?.key !== triggerKey) {
      const trigger = this.enabledTriggers.find(t => t.key === triggerKey);
      if (trigger) {await this.selectTrigger(trigger);}
    }
    if (workflowId) {
      const config = this.workflowConfigs.find(c => c.id === workflowId);
      if (config) {await this.selectWorkflowConfig(config);}
    }
  }

  private async refreshDataInBackground() {
    this.showLoadingSpinner = true;
    try {
      const [workflows, workflowTriggerTypes, actionTypes, addresses] = await Promise.all([
        this.workflowsProvider.getWorkflowsWithVersions(),
        this.workflowsProvider.getWorkflowTriggerTypes().catch(() => []),
        this.workflowsProvider.getWorkflowActionTypes().catch(() => []),
        this.client.getAddresses().catch(() => [])
      ]);
      this.hasProperties = addresses?.length > 0;
      this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      this.allWorkflows = workflows;
      this.workflowTriggerTypes = workflowTriggerTypes;
      this.loadActionTypes(actionTypes);
      this.buildAllTriggers();
      this.updateEnabledTriggers();

      if (this.selectedTrigger) {
        const currentTriggerKey = this.selectedTrigger.key;
        const refreshedTrigger = this.enabledTriggers.find(t => t.key === currentTriggerKey)
          || this.filteredCustomTriggers[0];
        if (refreshedTrigger) {
          await this.selectTrigger(refreshedTrigger);
        }
      }

      if (this.autoScheduleAddresses.length > 0) {
        const selectedId = this.selectedAutoScheduleAddress?.id;
        const scheduleAddresses = await this.client.getAddresses();
        this.autoScheduleAddresses = scheduleAddresses;
        this.filteredAutoScheduleAddresses = [...scheduleAddresses];
        this.updateVisibleAddresses();
        if (selectedId) {
          const addr = scheduleAddresses.find(a => a.id === selectedId);
          if (addr) {this.selectedAutoScheduleAddress = addr;}
        }
      }

      if (this.proSearchAddresses.length > 0) {
        const selectedId = this.selectedProSearchAddress?.id;
        const searchAddresses = await this.client.getMoreDetailAddresses();
        this.proSearchAddresses = searchAddresses;
        this.filteredProSearchAddresses = [...searchAddresses];
        this.proSearchAddressSelectItems = searchAddresses.map(a => ({
          viewValue: a.address_name || a.address1,
          value: a.id
        }));
        this.updateVisibleProSearchAddresses();
        if (selectedId) {
          const addr = searchAddresses.find(a => a.id === selectedId);
          if (addr) {this.selectedProSearchAddress = addr;}
        }
      }
    } catch (error) {
      // Silently fail — stale data is better than no data
    } finally {
      this.showLoadingSpinner = false;
    }
  }

  private async restoreNavigationState(savedState: {
    tab: string | null;
    triggerKey: string | null;
    workflowId: string | null;
    autoScheduleAddressId: string | null;
    proSearchAddressId: string | null;
  }) {
    localStorage.removeItem('unified_agent_tab');

    if (savedState.triggerKey) {
      const trigger = this.enabledTriggers.find(t => t.key === savedState.triggerKey);
      if (trigger) {
        await this.selectTrigger(trigger);

        if (this.isAutoScheduleTrigger(trigger) && savedState.autoScheduleAddressId) {
          const address = this.autoScheduleAddresses.find(
            a => String(a.id) === savedState.autoScheduleAddressId
          );
          if (address) {this.selectAutoSchedulePropertyChip(address);}
        } else if (this.isProSearchTrigger(trigger) && savedState.proSearchAddressId) {
          const address = this.proSearchAddresses.find(
            a => String(a.id) === savedState.proSearchAddressId
          );
          if (address) {
            this.proSearchAddressSelectForm.patchValue({ address: address.id }, { emitEvent: false });
            await this.selectProSearchAddress(address);
          }
        } else if (this.isMessageAgentTrigger(trigger)) {
          // No extra restore needed — selectTrigger already loaded message agent data
        } else if (savedState.workflowId) {
          const config = this.workflowConfigs.find(c => c.id === savedState.workflowId);
          if (config) {await this.selectWorkflowConfig(config);}
        }
        return;
      }
    }

    // Default: select guest_reservation.created trigger (rental only)
    const autoScheduleTrigger = this.isRentalClient
      ? this.enabledTriggers.find(t => this.isAutoScheduleTrigger(t))
      : null;
    if (autoScheduleTrigger) {
      await this.selectTrigger(autoScheduleTrigger);
    } else if (this.enabledTriggers.length > 0) {
      await this.selectTrigger(this.enabledTriggers[0]);
    }
  }

  ngOnDestroy() {
    this.autoScheduleSubscription?.unsubscribe();
    this.autoScheduleFormSubscription?.unsubscribe();
    if (this.updateAgentSubscription) {
      this.updateAgentSubscription.unsubscribe();
    }
    this.messageAgentSaveSubscription?.unsubscribe();
    this.messageAgentFormSubscription?.unsubscribe();
    this.formSubscriptions.forEach(sub => sub.unsubscribe());
  }

  async loadData() {
    try {
      const [workflows, workflowTriggerTypes, actionTypes, addresses] = await Promise.all([
        this.workflowsProvider.getWorkflowsWithVersions(),
        this.workflowsProvider.getWorkflowTriggerTypes().catch(() => []),
        this.workflowsProvider.getWorkflowActionTypes().catch(() => []),
        this.client.getAddresses().catch(() => [])
      ]);
      this.hasProperties = addresses?.length > 0;
      this.allWorkflows = workflows;
      this.workflowTriggerTypes = workflowTriggerTypes;
      this.loadActionTypes(actionTypes);
      this.buildAllTriggers();
      this.updateEnabledTriggers();
    } catch (error) {
      this.util.showError((error.error && error.error.message) ? error.error.message : error.message, 10000);
    }
  }

  buildAllTriggers() {
    this.allTriggers = [];

    // Group workflows by trigger type
    const triggerMap = new Map<string, { triggerName: string; triggerId: number; iconUrl: string; workflowIds: number[] }>();
    this.allWorkflows.forEach(workflow => {
      const version = workflow.published_version || workflow.draft_version;
      const versionTrigger = version?.workflow_version_trigger;
      if (!versionTrigger?.name) {return;}

      const triggerName = versionTrigger.name.trim();
      const key = triggerName.toLowerCase();
      if (triggerMap.has(key)) {
        triggerMap.get(key).workflowIds.push(workflow.id);
      } else {
        triggerMap.set(key, {
          triggerName,
          triggerId: versionTrigger.trigger_id,
          iconUrl: versionTrigger.icon_url,
          workflowIds: [workflow.id]
        });
      }
    });

    // Build trigger items from grouped workflows
    triggerMap.forEach((data, key) => {
      // Filter out "Message Created" — replaced by dedicated message agent triggers
      if (isHiddenAgentTrigger(key)) {return;}

      const matchingWt = (this.workflowTriggerTypes || []).find(
        wt => wt.id === data.triggerId || (wt.name || '').toLowerCase().trim() === key
      );
      const safeKey = `wf_${data.triggerId || key.replace(/\./g, '_')}`;
      this.allTriggers.push({
        key: `workflow.${data.triggerId || key}`,
        safeKey,
        title: data.triggerName,
        agentKey: 'workflow',
        agentTitle: matchingWt?.category || 'Automation',
        category: matchingWt?.category || 'Automation',
        enabled: true,
        instructions: '',
        blockActions: [],
        inputValues: {},
        allowedActions: [],
        sourceType: 'workflow',
        workflowTriggerId: data.triggerId ?? matchingWt?.id ?? null,
        dynamicInputs: matchingWt?.inputs ?? [],
        triggerOutputVariables: matchingWt?.output_variables ?? [],
        workflowIds: data.workflowIds,
        iconUrl: data.iconUrl || matchingWt?.icon_url || '',
        description: matchingWt?.description || ''
      });
    });

    // Add remaining trigger types from the API that aren't already represented by existing workflows
    const existingTriggerIds = new Set(
      this.allTriggers
        .filter(t => t.workflowTriggerId !== null && t.workflowTriggerId !== undefined && t.workflowTriggerId > 0)
        .map(t => t.workflowTriggerId)
    );
    (this.workflowTriggerTypes || []).forEach(wt => {
      if (wt.coming_soon) {return;}
      if (existingTriggerIds.has(wt.id)) {return;}
      if (isHiddenAgentTrigger(wt.name)) {return;}

      const safeKey = `wf_${wt.id}`;
      this.allTriggers.push({
        key: `workflow.${wt.id}`,
        safeKey,
        title: wt.name,
        agentKey: 'workflow',
        agentTitle: wt.category || 'Automation',
        category: wt.category || 'Automation',
        enabled: false,
        instructions: '',
        blockActions: [],
        inputValues: {},
        allowedActions: [],
        sourceType: 'workflow',
        workflowTriggerId: wt.id,
        dynamicInputs: wt.inputs ?? [],
        triggerOutputVariables: wt.output_variables ?? [],
        workflowIds: [],
        iconUrl: wt.icon_url || '',
        description: wt.description || ''
      });
    });

    // Force-enable auto-schedule triggers (only for rental clients)
    if (this.isRentalClient) {
      this.allTriggers.forEach(t => {
        if (this.isAutoScheduleTrigger(t)) {t.enabled = true;}
      });
    }

    // Add hard-coded Guest Reservation Created trigger (always present)
    const autoScheduleTriggerExists = this.allTriggers.some(t => this.isAutoScheduleTrigger(t));
    if (!autoScheduleTriggerExists) {
      this.allTriggers.push({
        key: 'custom.guest_reservation.created',
        safeKey: 'custom_guest_reservation_created',
        title: 'Guest Reservation Created',
        agentKey: 'guest_reservation',
        agentTitle: 'Guest Reservations',
        category: 'Guest Reservations',
        enabled: this.isRentalClient,
        instructions: '',
        blockActions: [],
        inputValues: {},
        allowedActions: [],
        sourceType: 'agent',
        workflowTriggerId: -2,
        dynamicInputs: [],
        triggerOutputVariables: [],
        workflowIds: [],
        iconUrl: '',
        description: 'Triggered when a guest reservation is created or synced.'
      });
    }

    // Add hard-coded Pro Search Initiated trigger (always present, frontend-only)
    const proSearchTriggerExists = this.allTriggers.some(t =>
      t.key === 'custom.pro_search.initiated'
    );
    if (!proSearchTriggerExists) {
      this.allTriggers.push({
        key: 'custom.pro_search.initiated',
        safeKey: 'custom_pro_search_initiated',
        title: 'Pro Search Initiated',
        agentKey: 'pro_search',
        agentTitle: 'Pros',
        category: 'Pros',
        enabled: true,
        instructions: '',
        blockActions: [],
        inputValues: {},
        allowedActions: [],
        sourceType: 'agent',
        workflowTriggerId: -1,
        dynamicInputs: [],
        triggerOutputVariables: [],
        workflowIds: [],
        iconUrl: 'assets/icon/searching.svg',
        description: 'Triggered when a pro search is initiated.'
      });
    }

    // Add hard-coded Pro Message Received trigger (always present)
    this.allTriggers.push({
      key: 'custom.message_agent.pro_message_received',
      safeKey: 'custom_message_agent_pro_message_received',
      title: 'Pro Message Received',
      agentKey: 'messages_agent',
      agentTitle: 'Messages',
      category: 'Messages',
      enabled: true,
      instructions: '',
      blockActions: [],
      inputValues: {},
      allowedActions: [],
      sourceType: 'agent',
      workflowTriggerId: -3,
      dynamicInputs: [],
      triggerOutputVariables: [],
      workflowIds: [],
      iconUrl: 'assets/img/chat.svg',
      description: 'Triggered when a message is received from a pro.'
    });

    // Add hard-coded Guest Message Received trigger (rental clients only)
    if (this.isRentalClient) {
      this.allTriggers.push({
        key: 'custom.message_agent.guest_message_received',
        safeKey: 'custom_message_agent_guest_message_received',
        title: 'Guest Message Received',
        agentKey: 'guest_messages_agent',
        agentTitle: 'Messages',
        category: 'Messages',
        enabled: true,
        instructions: '',
        blockActions: [],
        inputValues: {},
        allowedActions: [],
        sourceType: 'agent',
        workflowTriggerId: -4,
        dynamicInputs: [],
        triggerOutputVariables: [],
        workflowIds: [],
        iconUrl: 'assets/img/chat.svg',
        description: 'Triggered when a message is received from a guest.'
      });
    }
  }

  updateEnabledTriggers() {
    this.enabledTriggers = this.allTriggers.filter(t => t.enabled);
    this.filteredTriggers = [...this.enabledTriggers];
    this.filteredCustomTriggers = [...this.enabledTriggers];
  }

  filterTriggers(searchText: string) {
    if (!searchText || searchText.length < 2) {
      this.filteredCustomTriggers = [...this.enabledTriggers];
      this.filteredTriggers = [...this.enabledTriggers];
      return;
    }
    const lower = searchText.toLowerCase();
    this.filteredCustomTriggers = this.enabledTriggers.filter(t =>
      t.title.toLowerCase().includes(lower) || t.category?.toLowerCase().includes(lower)
    );
    this.filteredTriggers = [...this.filteredCustomTriggers];
  }

  async selectTrigger(trigger: TriggerItem) {
    this.isLoadingTrigger = true;
    this.selectedTrigger = trigger;
    localStorage.setItem('unified_agent_trigger_key', trigger.key);

    this.selectedActions.forEach((action, idx) => {
      if (action) {
        this.buildOutputsForAction(action, idx);
      }
    });

    this.resetActivityState();
    this.selectedPropertyChipId = null;
    this.isAutoScheduleMode = this.isRentalClient && this.isAutoScheduleTrigger(trigger);
    this.isPropertyChipSelected = this.isAutoScheduleMode;
    this.isProSearchMode = this.isProSearchTrigger(trigger);
    this.isMessageAgentMode = this.isMessageAgentTrigger(trigger);

    if (this.isAutoScheduleMode) {
      if (this.autoScheduleAddresses.length === 0) {
        await this.loadAutoScheduleData();
      }
      this.activityLoading = true;
      await this.buildFormForSelectedTrigger();
      await this.loadWorkflowConfigs();
      if (this.autoScheduleAddresses.length > 0 && !this.selectedPropertyChipId) {
        this.selectAutoSchedulePropertyChip(this.autoScheduleAddresses[0]);
      }
    } else if (this.isProSearchMode) {
      if (this.proSearchAddresses.length === 0) {
        await this.loadProSearchData();
      }
    } else if (this.isMessageAgentMode) {
      await this.loadMessageAgentData(trigger.agentKey);
      if (this.selectedTrigger !== trigger) {return;}
      this.loadMessageAgentActivity(trigger.agentKey);
    } else {
      this.activityLoading = true;
      await this.buildFormForSelectedTrigger();
      await this.loadWorkflowConfigs();
    }

    // A slower load for a trigger the customer has already left must not end the new one's loading state.
    if (this.selectedTrigger !== trigger) {return;}
    this.isLoadingTrigger = false;
    if (this.isAutoScheduleMode && !this.showAllAutoScheduleChips) {
      this.updateVisibleAddresses();
    }
    if (!this.showAllWorkflowChips) {
      this.updateVisibleWorkflowConfigs();
    }
  }

  selectAutoSchedulePropertyChip(address: any) {
    this.selectedPropertyChipId = address.id;
    this.isPropertyChipSelected = true;
    this.activeWorkflowConfigId = null;
    this.resetActivityState();
    this.selectAutoScheduleAddress(address);
  }

  async selectAutoScheduleWorkflowChip(config: WorkflowConfig) {
    this.selectedPropertyChipId = null;
    this.isPropertyChipSelected = false;
    await this.selectWorkflowConfig(config);
  }

  async onProSearchAddressSelectChange(addressId: number) {
    const address = this.proSearchAddresses.find(a => a.id === addressId);
    if (address) {
      await this.selectProSearchAddress(address);
    }
  }

  get activityCanLoadMore(): boolean {
    return !this.activityLoading && this.activityRuns.length < this.activityTotalRecords;
  }

  private resetActivityState() {
    this.activityRequestSeq++;
    this.activityRuns = [];
    this.activityRows = [];
    this.activitySummary = null;
    this.activityTotalRecords = 0;
    this.activityFailedRecords = null;
    this.activityPage = 1;
    this.activityFilterWorkflowId = null;
    this.activityFilterName = '';
    this.activityLoading = false;
    this.activityLoadingMore = false;
  }

  private allWorkflowIds(): number[] {
    return this.allWorkflows.map(w => Number(w.id)).filter(id => !isNaN(id));
  }

  private buildActivityAgentOptions() {
    this.activityAgentOptions = this.allWorkflows
      .filter(w => w.id && w.name)
      .map(w => ({ value: String(w.id), viewValue: w.name }))
      .sort((a, b) => a.viewValue.localeCompare(b.viewValue));
  }

  filterActivityByWorkflowId(workflowId: string) {
    const option = this.activityAgentOptions.find(o => o.value === workflowId);
    if (!option) {
      return;
    }
    this.filterActivityByWorkflow(Number(option.value), option.viewValue);
  }

  /** The feed is every agent's runs on the account, narrowed to one agent while a filter is set. */
  private activityWorkflowIds(): number[] {
    if (this.activityFilterWorkflowId !== null) {
      return [this.activityFilterWorkflowId];
    }
    return this.allWorkflowIds();
  }

  async loadActivity(append = false) {
    if (this.selectedTrigger && (this.isProSearchTrigger(this.selectedTrigger)
        || this.isMessageAgentTrigger(this.selectedTrigger))) {
      this.resetActivityState();
      return;
    }
    this.buildActivityAgentOptions();

    const workflowIds = this.activityWorkflowIds();
    if (!workflowIds.length) {
      this.resetActivityState();
      return;
    }

    // Trigger and agent switches can overlap; only the newest request may land.
    const seq = ++this.activityRequestSeq;
    const page = append ? this.activityPage + 1 : 1;
    if (append) {
      this.activityLoadingMore = true;
    } else {
      this.activityLoading = true;
    }
    try {
      const today = LuxonDateTime.local();
      const startDate = this.activityStartDate || '2023-8-10';
      const endDate = this.activityEndDate || today.toISODate();
      const [response, failedResponse] = await Promise.all([
        this.workflowsProvider.getWorkflowVersionRuns(workflowIds, startDate, endDate, page, this.activityPerPage),
        append ? null : this.workflowsProvider.getWorkflowVersionRuns(workflowIds, startDate, endDate, 1, 1, null, 'failed'),
      ]);
      if (seq !== this.activityRequestSeq) {
        return;
      }
      const runs: any[] = response.body || [];
      const seen = new Set(this.activityRuns.map(r => r.id));
      this.activityRuns = append ? [...this.activityRuns, ...runs.filter(r => !seen.has(r.id))] : runs;
      this.activityTotalRecords = Number(response.totalRecords) || this.activityRuns.length;
      if (failedResponse) {
        const failed = Number(failedResponse.totalRecords);
        this.activityFailedRecords = isNaN(failed) ? null : failed;
      }
      this.activityPage = page;
    } catch {
      if (seq !== this.activityRequestSeq) {
        return;
      }
      if (!append) {
        this.activityRuns = [];
        this.activityTotalRecords = 0;
        this.activityFailedRecords = null;
        this.activityPage = 1;
      }
    }
    this.activityRows = toActivityRows(this.activityRuns);
    this.activitySummary = toActivitySummary(this.activityRows, this.activityTotalRecords, this.activityFailedRecords);
    this.activityLoading = false;
    this.activityLoadingMore = false;
  }

  filterActivityByWorkflow(workflowId: number, name: string) {
    this.activityFilterWorkflowId = workflowId;
    this.activityFilterName = name;
    this.loadActivity();
  }

  clearActivityFilter() {
    if (this.activityFilterWorkflowId === null) {
      return;
    }
    this.activityFilterWorkflowId = null;
    this.activityFilterName = '';
    this.loadActivity();
  }

  loadMoreActivity() {
    if (this.activityLoading || this.activityLoadingMore || !this.activityCanLoadMore) {
      return;
    }
    this.loadActivity(true);
  }

  selectActivityStringDateRange(selection: string) {
    const today = LuxonDateTime.local();
    this.activityEndDate = today.toISODate();
    if (selection === 'last_week') {
      this.activityStartDate = today.minus({ days: 7 }).toISODate();
    } else if (selection === 'last_month') {
      this.activityStartDate = today.minus({ months: 1 }).toISODate();
    } else if (selection === 'all_time') {
      this.activityStartDate = '2023-8-10';
    } else if (selection === 'today') {
      this.activityStartDate = today.toISODate();
    }
    if (this.isMessageAgentMode && this.selectedTrigger) {
      this.loadMessageAgentActivity(this.selectedTrigger.agentKey);
    } else {
      this.loadActivity();
    }
  }

  selectActivityCustomDateRange(selection: { start: string; end: string }) {
    const start = new Date(selection.start);
    const end = new Date(selection.end);
    this.activityStartDate = LuxonDateTime.fromJSDate(start).toISODate();
    this.activityEndDate = LuxonDateTime.fromJSDate(end).toISODate();
    if (this.isMessageAgentMode && this.selectedTrigger) {
      this.loadMessageAgentActivity(this.selectedTrigger.agentKey);
    } else {
      this.loadActivity();
    }
  }

  goToWorkflowRun(run: any) {
    const params = { run, selectedWorkflow: '' };
    this.rightSidePanelService.navigateTo(
      'automations/workflow-history/workflow-run', params, WorkflowRunPage
    );
  }

  isAutoScheduleTrigger(trigger: TriggerItem): boolean {
    if (!trigger) {return false;}
    const titleLower = (trigger.title || '').toLowerCase();
    const keyLower = (trigger.key || '').toLowerCase();
    return (titleLower.includes('guest reservation') && titleLower.includes('created')) ||
      keyLower.includes('guest_reservation.created') ||
      keyLower.includes('guest_reservation.synced');
  }

  isProSearchTrigger(trigger: TriggerItem): boolean {
    if (!trigger) {return false;}
    const keyLower = (trigger.key || '').toLowerCase();
    return keyLower.includes('pro_search.initiated');
  }

  isMessageAgentTrigger(trigger: TriggerItem): boolean {
    if (!trigger) {return false;}
    const keyLower = (trigger.key || '').toLowerCase();
    return keyLower === 'custom.message_agent.pro_message_received'
      || keyLower === 'custom.message_agent.guest_message_received';
  }

  private async loadMessageAgentData(agentKey: string) {
    try {
      await this.agentsProvider.clearAgentSettingsCache();
      const allAgents = await this.agentsProvider.getAgentSettings();
      if (this.selectedTrigger?.agentKey !== agentKey) {return;}
      this.messageAgentSettings = allAgents.find(a => a.key === agentKey)
        || { key: agentKey, user_settings: { enabled: false, instructions: '' } };

      this.messageAgentFormSubscription?.unsubscribe();

      const instructions = this.messageAgentSettings?.user_settings?.instructions || '';
      const enabled = this.messageAgentSettings?.user_settings?.enabled ?? false;
      const blockedActions = this.messageAgentSettings?.user_settings?.blocked_actions || [];

      this.messageAgentBlockedActionItems = (this.messageAgentSettings?.blockable_actions || []).map(action => ({
        viewValue: snakeCaseToTitleCase(action),
        value: action
      }));

      this.messageAgentInstructionsForm.patchValue({ instructions, blockedActions }, { emitEvent: false });
      this.messageAgentEnabled = enabled;
      this.messageAgentLoadedSnapshot = { enabled, instructions, blocked_actions: blockedActions };

      this.messageAgentFormSubscription = this.messageAgentInstructionsForm.get('instructions')
        .valueChanges
        .pipe(debounceTime(600))
        .subscribe(() => this.updateMessageAgentSubject.next());
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    }
  }

  private async performMessageAgentSave() {
    if (!this.selectedTrigger || !this.isMessageAgentMode || !this.messageAgentSettings) {return;}
    const save = {
      settings: this.messageAgentSettings,
      // The agent the form was loaded for, not selectedTrigger, which changes as soon as
      // the customer clicks another trigger and before its settings load.
      agentKey: this.messageAgentSettings.key || this.selectedTrigger.agentKey,
      payload: {
        enabled: this.messageAgentEnabled,
        instructions: this.messageAgentInstructionsForm.get('instructions')?.value || '',
        blocked_actions: this.messageAgentInstructionsForm.get('blockedActions')?.value || []
      }
    };
    // The snapshot is what was loaded, not what a save still out will store: compare
    // with it only when nothing for this agent is waiting or in flight.
    const pending = this.messageAgentSaveSending === save.settings
      || this.messageAgentSaveQueue.some(queued => queued.settings === save.settings);
    if (!pending && this.matchesMessageAgentSnapshot(save.payload)) {return;}
    const last = this.messageAgentSaveQueue[this.messageAgentSaveQueue.length - 1];
    if (last?.settings === save.settings) {
      this.messageAgentSaveQueue[this.messageAgentSaveQueue.length - 1] = save;
    } else {
      this.messageAgentSaveQueue.push(save);
    }
    if (this.messageAgentSaveInFlight) {return;}
    this.messageAgentSaveInFlight = true;
    try {
      while (this.messageAgentSaveQueue.length) {
        const next = this.messageAgentSaveQueue.shift();
        this.messageAgentSaveSending = next.settings;
        await this.sendMessageAgentSettings(next);
      }
    } finally {
      this.messageAgentSaveInFlight = false;
      this.messageAgentSaveSending = null;
    }
  }

  // Nothing actually changed since load → don't write. Guards against any
  // stray form emission on landing/navigation creating a per-client
  // settings row the client never asked for.
  private matchesMessageAgentSnapshot(payload: { enabled: boolean; instructions: string; blocked_actions: string[] }): boolean {
    return !!this.messageAgentLoadedSnapshot
      && payload.enabled === this.messageAgentLoadedSnapshot.enabled
      && payload.instructions === this.messageAgentLoadedSnapshot.instructions
      && JSON.stringify(payload.blocked_actions) === JSON.stringify(this.messageAgentLoadedSnapshot.blocked_actions);
  }

  private async sendMessageAgentSettings(save: { settings: any; agentKey: string; payload: { enabled: boolean; instructions: string; blocked_actions: string[] } }) {
    const stillLoaded = this.messageAgentSettings === save.settings;
    // A queued save can match what the one before it just stored.
    if (stillLoaded && this.matchesMessageAgentSnapshot(save.payload)) {return;}

    this.isSaving = true;
    try {
      await this.agentsProvider.updateAgentSettings(save.agentKey, save.payload);
      await this.agentsProvider.clearAgentSettingsCache();

      save.settings.user_settings = {
        enabled: save.payload.enabled,
        instructions: save.payload.instructions,
        blocked_actions: save.payload.blocked_actions
      };
      // Once the customer has moved to another agent, the snapshot is that agent's.
      if (this.messageAgentSettings === save.settings) {
        this.messageAgentLoadedSnapshot = { ...save.payload };
      }
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.isSaving = false;
    }
  }

  toggleMessageAgentEnabled() {
    this.messageAgentEnabled = !this.messageAgentEnabled;
    this.updateMessageAgentSubject.next();
  }

  updateMessageAgentBlockedActions() {
    this.updateMessageAgentSubject.next();
  }

  private async loadMessageAgentActivity(agentKey: string) {
    this.messageAgentActivityLoading = true;
    this.activityLoading = true;
    try {
      this.messageAgentActivity = await this.agentsProvider.getAgentActivity(agentKey, 1, 20);
    } catch {
      this.messageAgentActivity = [];
    } finally {
      this.messageAgentActivityLoading = false;
      this.activityLoading = false;
    }
  }

  async buildFormForSelectedTrigger() {
    this.formSubscriptions.forEach(sub => sub.unsubscribe());
    this.formSubscriptions = [];
    this.isFormInitializing = true;
    this.dynamicInputsLoaded = false;
    this.radioItems = {};

    const trigger = this.selectedTrigger;
    if (!trigger) {
      this.isFormInitializing = false;
      return;
    }

    this.form.patchValue({
      instructions: trigger.instructions,
      blockedActions: trigger.blockActions
    }, { emitEvent: false });

    this.blockedActionItems = trigger.allowedActions.map(action => ({
      viewValue: action.title,
      value: action.key
    }));

    // Remove old special field and dynamic controls
    Object.keys(this.form.controls).forEach(key => {
      if (key !== 'instructions' && key !== 'blockedActions') {
        this.form.removeControl(key);
      }
    });

    // Add special trigger fields
    const specialFields = this.specialTriggerFields[trigger.key];
    if (specialFields) {
      specialFields.forEach(field => {
        const defaultValue = this.getDefaultValueForField(field);
        const value = trigger.inputValues?.[field] !== undefined
          ? trigger.inputValues[field]
          : defaultValue;
        this.form.addControl(field, this.fb.control(value));

        const sub = this.form.get(field).valueChanges
          .subscribe(() => this.updateAgent());
        this.formSubscriptions.push(sub);
      });
    }

    // Add dynamic input form controls
    if (trigger.dynamicInputs?.length) {
      for (const input of trigger.dynamicInputs) {
        if (specialFields?.includes(input.key)) {continue;}
        const savedValue = trigger.inputValues?.[input.key];
        const isAllValue = savedValue === undefined || savedValue === null || savedValue === '' || (Array.isArray(savedValue) && savedValue.length === 0);
        const value = isAllValue ? '' : savedValue;
        this.form.addControl(input.key, this.fb.control(value));
        if ((input.form?.type === 'multi_select' || input.form?.type === 'select' || input.key === 'assigned_to_concierge') && !this.allSpecialFieldNames.has(input.key)) {
          this.form.addControl(
            `${input.key}-radio`,
            this.fb.control(isAllValue ? '' : this.getAdjustedLabel((input.form?.label || '').toLowerCase()))
          );
        }
      }
    }

    // Subscribe to instructions and blocked actions changes
    const instrSub = this.form.get('instructions').valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => this.updateAgent());
    this.formSubscriptions.push(instrSub);

    const blockSub = this.form.get('blockedActions').valueChanges
      .subscribe(() => this.updateAgent());
    this.formSubscriptions.push(blockSub);

    // Subscribe to workflow name changes
    const nameSub = this.workflowNameForm.get('workflowName').valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => this.updateAgent());
    this.formSubscriptions.push(nameSub);

    // Subscribe to tag changes (separate FormArray on the same form). Categories
    // live on the workflow, not the version, so they save via a workflow-only
    // path — saving a version draft here would create a pointless draft and, on
    // BE-protected agents, fail with "This workflow cannot be modified".
    const tagsSub = this.tagsFormArray.valueChanges
      .pipe(debounceTime(500))
      .subscribe(() => this.updateAgentTagsOnly());
    this.formSubscriptions.push(tagsSub);

    // Subscribe to dynamic input changes
    if (trigger.dynamicInputs?.length) {
      for (const input of trigger.dynamicInputs) {
        if (specialFields?.includes(input.key)) {continue;}
        const isTextInput = input.form?.type === 'number' || input.form?.type === 'text' || input.form?.type === 'textarea';
        const dynSub = isTextInput
          ? this.form.get(input.key)?.valueChanges
              .pipe(debounceTime(500))
              .subscribe(() => this.updateAgent())
          : this.form.get(input.key)?.valueChanges
              .subscribe(() => this.updateAgent());
        if (dynSub) {this.formSubscriptions.push(dynSub);}
        const radioKey = `${input.key}-radio`;
        const radioSub = this.form.get(radioKey)?.valueChanges
          .subscribe(() => this.updateAgent());
        if (radioSub) {this.formSubscriptions.push(radioSub);}
      }
    }

    // Fetch dynamic input options from API
    await this.getDynamicInputs();
    // Note: isFormInitializing stays true here — it will be set to false
    // by selectWorkflowConfig() or autoCreateWorkflowForTrigger() in loadWorkflowConfigs(),
    // preventing premature auto-save during the gap between these methods.
  }

  toggleAddTriggerDropdown() {
    this.showAddTriggerDropdown = !this.showAddTriggerDropdown;
    this.addTriggerSearchText = '';
    this.updateAvailableTriggers();
  }

  closeAddTriggerDropdown() {
    this.showAddTriggerDropdown = false;
    this.addTriggerSearchText = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showAddTriggerDropdown) {
      return;
    }
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.add-trigger-dropdown');
    const button = target.closest('.create-button');
    if (!dropdown && !button) {
      this.closeAddTriggerDropdown();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    if (this.isAutoScheduleMode && !this.showAllAutoScheduleChips) {
      this.updateVisibleAddresses();
    }
    if (this.isProSearchMode && !this.showAllProSearchChips) {
      this.updateVisibleProSearchAddresses();
    }
  }

  updateAvailableTriggers() {
    if (!this.addTriggerSearchText) {
      this.availableTriggers = [...this.allTriggers];
      return;
    }
    const lower = this.addTriggerSearchText.toLowerCase();
    this.availableTriggers = this.allTriggers.filter(t =>
      t.title.toLowerCase().includes(lower) ||
      t.category?.toLowerCase().includes(lower)
    );
  }

  onAddTriggerSearch() {
    this.updateAvailableTriggers();
  }

  goToAddProperty() {
    this.router.navigate(['/properties']);
  }

  async toggleTrigger(trigger: TriggerItem) {
    if (!this.hasProperties) {
      this.closeAddTriggerDropdown();
      this.util.showAlert('No Properties', 'No properties added. Please add a property to set up agents.');
      return;
    }

    // Frontend-only triggers (like Pro Search Initiated, Guest Reservation Created, Message Agents) cannot be toggled
    // Exception: non-rental users can toggle Guest Reservation (-2) since it starts disabled for them
    if (trigger.workflowTriggerId === -1 || trigger.workflowTriggerId === -3 || trigger.workflowTriggerId === -4
        || (trigger.workflowTriggerId === -2 && this.isRentalClient)) {
      this.closeAddTriggerDropdown();
      this.selectTrigger(trigger);
      return;
    }

    trigger.enabled = !trigger.enabled;
    this.updateEnabledTriggers();

    if (trigger.enabled) {
      this.selectTrigger(trigger);
    } else if (this.selectedTrigger?.key === trigger.key && this.selectedTrigger?.agentKey === trigger.agentKey) {
      this.selectedTrigger = null;
    }

    this.closeAddTriggerDropdown();
  }

  updateAgent() {
    if (this.isFormInitializing) {return;}
    this.updateAgentSubject.next();
  }

  // Persists ONLY the category tags (a workflow-level field). No workflow
  // version is written, so this works on agents whose versions the BE protects.
  private async updateAgentTagsOnly(): Promise<void> {
    if (this.isFormInitializing) {return;}
    // A brand-new agent has no workflow yet — fall back to the normal path,
    // which creates the workflow (and its first version) first.
    if (!this.activeWorkflowConfigId) {
      this.updateAgent();
      return;
    }
    const workflowId = Number(this.activeWorkflowConfigId);
    const config = this.workflowConfigs.find(c => c.id === this.activeWorkflowConfigId);
    if (!config) {return;}

    try {
      const wf = this.allWorkflows.find(w => String(w.id) === this.activeWorkflowConfigId);
      const freshWf = await this.workflowsProvider.getWorkflow(workflowId).catch(() => null);
      const existingTags = freshWf?.tags || wf?.tags || [];
      const mergedTags = this.mergeWithSystemTags(this.getTagsValue(), existingTags);
      const tagsChanged = mergedTags.length !== existingTags.length
        || mergedTags.some((t, i) => t !== existingTags[i]);
      if (!tagsChanged) {return;}

      this.isSaving = true;
      await this.workflowsProvider.updateWorkflow(
        { name: freshWf?.name || wf?.name || config.name, description: '', tags: mergedTags },
        workflowId
      );
      if (wf) {wf.tags = mergedTags;}
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.isSaving = false;
    }
  }

  async performUpdateAgent() {
    if (!this.selectedTrigger) {return;}
    if (this.isSavingWorkflowVersion) {return;}

    this.isSaving = true;
    this.isSavingWorkflowVersion = true;

    try {
      const name = this.workflowNameForm.get('workflowName')?.value?.trim();

      if (!this.activeWorkflowConfigId) {
        if (!name) {return;}
        await this.autoCreateWorkflowForTrigger();
        return;
      }

      const workflowId = Number(this.activeWorkflowConfigId);
      const config = this.workflowConfigs.find(c => c.id === this.activeWorkflowConfigId);
      if (!config) {return;}

      const wf = this.allWorkflows.find(w => String(w.id) === this.activeWorkflowConfigId);
      const freshWf = await this.workflowsProvider.getWorkflow(workflowId).catch(() => null);
      const existingTags = freshWf?.tags || wf?.tags || [];
      const mergedTags = this.mergeWithSystemTags(this.getTagsValue(), existingTags);
      const nameChanged = !!(name && name !== config.name);
      const tagsChanged = mergedTags.length !== existingTags.length
        || mergedTags.some((t, i) => t !== existingTags[i]);
      if (nameChanged || tagsChanged) {
        await this.workflowsProvider.updateWorkflow(
          { name: name || config.name, description: '', tags: mergedTags },
          workflowId
        );
        if (nameChanged) {config.name = name;}
        if (wf) {wf.tags = mergedTags;}
      }

      const versionPayload = this.buildVersionPayload(workflowId);
      if (versionPayload) {
        const newVersion = await this.workflowsProvider.createWorkflowVersion(versionPayload);
        this.activeWorkflowLatestVersionId = newVersion.id;
        this.latestVersion = newVersion;
        // Refresh trigger/action output_variables from the new version so
        // subsequent chip clicks use the current step IDs. This prevents
        // publish errors like "Variable not found: {{3.job.id}}" caused by
        // stale step-id prefixes drifting between autosaves.
        this.refreshOutputsFromVersion(newVersion);
      }

      config.inputValues = this.gatherInputValues();
      config.instructions = this.form.get('instructions')?.value || '';
      config.blockActions = this.form.get('blockedActions')?.value || [];
      config.enabled = this.isAutomationEnabled;

      this.isNewWorkflowMode = false;
      this.buildTooltipLinesForConfigs();
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.isSavingWorkflowVersion = false;
      setTimeout(() => this.isSaving = false, 500);
    }
  }

  async loadWorkflowConfigs() {
    if (!this.selectedTrigger) {
      return;
    }

    const triggerTitle = this.selectedTrigger.title.toLowerCase().trim();
    const matchingWorkflows = this.allWorkflows.filter(w => {
      const version = w.published_version || w.draft_version;
      const triggerName = version?.workflow_version_trigger?.name;
      return triggerName?.toLowerCase().trim() === triggerTitle;
    });

    this.workflowConfigs = matchingWorkflows.map(w => {
      const version = w.published_version || w.draft_version;
      return {
        id: String(w.id),
        name: w.name,
        enabled: !w.paused_at,
        inputValues: version?.workflow_version_trigger?.input_values || {},
        instructions: '',
        blockActions: [],
      };
    });

    this.buildTooltipLinesForConfigs();
    this.showAllWorkflowChips = false;
    this.isNewWorkflowMode = false;
    if (this.workflowConfigs.length > 0) {
      await this.selectWorkflowConfig(this.workflowConfigs[0]);
    } else {
      this.activeWorkflowConfigId = null;
      this.loadActivity();
      this.isAutomationEnabled = false;
      this.isViewingPublished = false;
      this.resetActions();
      this.patchTagsForm(null);
      this.isFormInitializing = false;
    }
  }

  async selectWorkflowConfig(config: WorkflowConfig) {
    this.activeWorkflowConfigId = config.id;
    localStorage.setItem('unified_agent_workflow_id', config.id);
    this.isNewWorkflowMode = false;
    this.filterActivityByWorkflow(Number(config.id), config.name);

    const wf = this.allWorkflows.find(w => String(w.id) === config.id);
    if (wf?.published_version) {
      await this.loadPublishedView(wf);
      return;
    }

    this.isViewingPublished = false;
    this.activeWorkflowData = wf || null;
    this.isFormInitializing = true;
    this.isAutomationEnabled = config.enabled !== false;
    this.resetActions();
    this.workflowNameForm.patchValue({ workflowName: config.name }, { emitEvent: false });
    this.patchTagsForm(wf?.tags);
    this.form.patchValue({
      instructions: config.instructions,
      blockedActions: config.blockActions
    }, { emitEvent: false });
    const specialFields = this.specialTriggerFields[this.selectedTrigger?.key];
    if (specialFields && config.inputValues) {
      specialFields.forEach(field => {
        if (this.form.get(field) && config.inputValues[field] !== undefined) {
          this.form.get(field).setValue(config.inputValues[field], { emitEvent: false });
        }
      });
    }
    if (this.selectedTrigger?.dynamicInputs?.length && config.inputValues) {
      this.selectedTrigger.dynamicInputs.forEach(input => {
        if (specialFields?.includes(input.key)) {return;}
        if (this.form.get(input.key) && config.inputValues[input.key] !== undefined) {
          const value = config.inputValues[input.key];
          this.form.get(input.key)?.setValue(value, { emitEvent: false });
          const radioKey = `${input.key}-radio`;
          if (this.form.get(radioKey)) {
            const isAll = value === null || value === '' || (Array.isArray(value) && value.length === 0);
            this.form.get(radioKey).setValue(isAll ? '' : this.getAdjustedLabel((input.form?.label || '').toLowerCase()), { emitEvent: false });
          }
        }
      });
    }
    await this.loadActionsForWorkflow(config.id);
    this.isFormInitializing = false;
  }

  private patchTriggerInputsFromVersion(version: any) {
    const triggerInputValues = version?.workflow_version_trigger?.input_values;
    if (!triggerInputValues || !this.selectedTrigger?.dynamicInputs?.length) {return;}

    const specialFields = this.specialTriggerFields[this.selectedTrigger?.key];
    if (specialFields) {
      specialFields.forEach(field => {
        if (this.form.get(field) && triggerInputValues[field] !== undefined) {
          this.form.get(field).setValue(triggerInputValues[field], { emitEvent: false });
        }
      });
    }

    this.selectedTrigger.dynamicInputs.forEach(input => {
      if (specialFields?.includes(input.key)) {return;}
      if (this.form.get(input.key) && triggerInputValues[input.key] !== undefined) {
        const value = triggerInputValues[input.key];
        this.form.get(input.key)?.setValue(value, { emitEvent: false });
        const radioKey = `${input.key}-radio`;
        if (this.form.get(radioKey)) {
          const isAll = value === null || value === '' || (Array.isArray(value) && value.length === 0);
          this.form.get(radioKey).setValue(isAll ? '' : this.getAdjustedLabel((input.form?.label || '').toLowerCase()), { emitEvent: false });
        }
      }
    });
  }

  private async loadActionsForWorkflow(workflowId: string) {
    try {
      const versions = await this.workflowsProvider.getWorkflowVersion(Number(workflowId));
      if (!versions?.length) {return;}

      const version = versions[0];
      this.activeWorkflowLatestVersionId = version.id;
      this.latestVersion = version;

      this.patchTriggerInputsFromVersion(version);

      // Use the saved workflow version's output_variables as the source of truth
      // (the trigger-types API doesn't include output_variables for every trigger)
      const versionTriggerOutputs = version?.workflow_version_trigger?.output_variables;
      if (this.selectedTrigger && versionTriggerOutputs?.length) {
        this.selectedTrigger.triggerOutputVariables = versionTriggerOutputs;
      }

      const versionActions = version.workflow_version_actions || [];

      if (versionActions.length === 0) {return;}

      this.selectedActions = [];
      this.actionForms = [];
      this.actionFormIds = [];

      for (const savedAction of versionActions) {
        const actionType = this.allActionsData.find(a => a.id === savedAction.action_id);
        if (!actionType) {continue;}

        // Pass the saved receiver_type so the BE returns contextualized
        // input_options and input_default_value (e.g. the populated team
        // member list when receiver_type is customer_member). Without this,
        // editing a previously-saved Team Member workflow leaves
        // to_customer_members hidden because its default stays {{hidden_input}}.
        const savedReceiverType = savedAction.input_values?.receiver_type;
        const inputDataPayload = savedReceiverType
          ? JSON.stringify({ receiver_type: savedReceiverType })
          : undefined;
        const inputData = await this.workflowsProvider.getInputData(
          actionType.id,
          'Action',
          inputDataPayload
        );
        actionType.inputData = inputData || [];
        actionType.inputs?.forEach(input => {
          // Bypass getActionSelectOptions' input.options early-return — the
          // cached allActionsData entry may carry stale options from a prior
          // load with a different receiver_type.
          const inputDataItem = inputData?.find(d => d?.input_key === input?.key);
          input.options = inputDataItem
            ? (inputDataItem.input_options || []).map(option => ({
                ...option,
                value: option.value,
                viewValue: option.label,
              }))
            : (input.options || []);
        });

        // Attach the saved action's output_variables so they can be referenced
        // as prior-action outputs by subsequent actions in buildOutputsForAction.
        if (savedAction.output_variables?.length) {
          actionType.output_variables = savedAction.output_variables;
        }

        const newFormId = Math.random().toString(36).substring(7);
        const newForm = this.fb.group({});
        actionType.inputs?.forEach(input => {
          const savedValue = savedAction.input_values?.[input.key] || '';
          newForm.addControl(input.key, this.fb.control(savedValue));
        });

        this.buildOutputsForAction(actionType, this.selectedActions.length);
        this.selectedActions.push(actionType);
        this.actionForms.push(newForm);
        this.actionFormIds.push(newFormId);

        const actionSub = newForm.valueChanges
          .subscribe(() => this.updateAgent());
        this.formSubscriptions.push(actionSub);
      }

      setTimeout(() => {
        this.isFormInitializing = true;
        versionActions.forEach((savedAction, index) => {
          const formId = this.actionFormIds[index];
          if (!formId || !savedAction?.input_values) {return;}

          const action = this.selectedActions[index];
          const baseChips = this.buildChipsForAction(action);

          const chipTextAreas = this.chipTextAreaComponents?.filter(
            c => c.id === formId
          );
          chipTextAreas?.forEach(chipTextArea => {
            const savedContent = savedAction.input_values[chipTextArea.inputKey];
            if (savedContent) {
              // Rewrite any stale step-id prefixes in the saved content to
              // match the current version's keys, so re-saves don't publish
              // broken variable refs like {{3.job.id}}.
              const content = this.rewriteContentToCurrentKeys(savedContent, baseChips);
              // Ensure every {{...}} placeholder in the content has a
              // matching chip so parseAndSetBody can convert it back to a chip.
              const chips = this.chipsFromContent(content, baseChips);
              chipTextArea.body = { content, chips };
              chipTextArea.parseAndSetBody();
            }
          });
        });
        this.isFormInitializing = false;
      }, 500);
    } catch (error) {
      // Silently fail — action loading is supplementary
    }
  }

  async startNewWorkflow() {
    this.isViewingPublished = false;
    this.isFormInitializing = true;
    this.activeWorkflowConfigId = null;
    this.clearActivityFilter();
    this.activeWorkflowData = null;
    this.selectedPropertyChipId = null;
    this.isPropertyChipSelected = false;
    this.activeWorkflowLatestVersionId = null;
    this.latestVersion = null;
    this.isNewWorkflowMode = true;
    this.isAutomationEnabled = false;
    this.resetActions();
    this.patchTagsForm(null);
    this.form.patchValue({ instructions: '', blockedActions: [] }, { emitEvent: false });
    const specialFields = this.specialTriggerFields[this.selectedTrigger?.key];
    if (specialFields) {
      specialFields.forEach(field => {
        if (this.form.get(field)) {
          this.form.get(field).setValue(this.getDefaultValueForField(field), { emitEvent: false });
        }
      });
    }
    if (this.selectedTrigger?.dynamicInputs?.length) {
      this.selectedTrigger.dynamicInputs.forEach(input => {
        if (specialFields?.includes(input.key)) {return;}
        this.form.get(input.key)?.setValue('', { emitEvent: false });
        const radioKey = `${input.key}-radio`;
        this.form.get(radioKey)?.setValue('', { emitEvent: false });
      });
    }
    this.isFormInitializing = false;
    await this.autoCreateWorkflowForTrigger();
  }

  focusWorkflowTitle() {
    setTimeout(() => {
      this.workflowTitleInput?.focusNativeInput();
    }, 0);
  }

  private async createNewWorkflowOnBackend(name: string, tags: string[] = []): Promise<number> {
    const payload = { name, description: '', tags };
    const workflow = await this.workflowsProvider.createWorkflow(payload);
    await this.workflowsProvider.pauseWorkflow(workflow.id);
    return workflow.id;
  }

  private async autoCreateWorkflowForTrigger() {
    if (this.isCreatingWorkflow) {return;}
    this.isCreatingWorkflow = true;
    this.isSaving = true;

    try {
      const defaultName = `Agent ${this.allWorkflows.length + 1}`;
      const tags = this.getTagsValue();
      const workflowId = await this.createNewWorkflowOnBackend(defaultName, tags);

      const finalName = `Agent ${workflowId}`;
      const freshWf = await this.workflowsProvider.getWorkflow(workflowId).catch(() => null);
      const mergedTags = this.mergeWithSystemTags(tags, freshWf?.tags);
      await this.workflowsProvider.updateWorkflow({ name: finalName, description: '', tags: mergedTags }, workflowId);

      this.workflowNameForm.patchValue({ workflowName: finalName }, { emitEvent: false });

      const versionPayload = this.buildVersionPayload(workflowId);
      if (versionPayload) {
        const newVersion = await this.workflowsProvider.createWorkflowVersion(versionPayload);
        this.activeWorkflowLatestVersionId = newVersion.id;
        this.latestVersion = newVersion;
      }

      const config: WorkflowConfig = {
        id: String(workflowId),
        name: finalName,
        instructions: '',
        blockActions: [],
        inputValues: this.gatherInputValues(),
        enabled: false,
      };

      this.workflowConfigs.push(config);
      this.activeWorkflowConfigId = String(workflowId);
      this.isNewWorkflowMode = false;
      this.filterActivityByWorkflow(Number(workflowId), finalName);

      const newWorkflow = {
        id: workflowId,
        name: finalName,
        paused_at: new Date().toISOString(),
        draft_version: {
          workflow_version_trigger: {
            name: this.selectedTrigger.title,
            input_values: this.gatherInputValues(),
          }
        }
      };
      this.allWorkflows.push(newWorkflow);
      this.activeWorkflowData = newWorkflow;
      if (this.selectedTrigger && !this.selectedTrigger.workflowIds.includes(workflowId)) {
        this.selectedTrigger.workflowIds.push(workflowId);
      }

      this.buildTooltipLinesForConfigs();
      this.updateVisibleWorkflowConfigs();
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
      this.workflowNameForm.reset();
      this.focusWorkflowTitle();
    } finally {
      this.isCreatingWorkflow = false;
      setTimeout(() => this.isSaving = false, 500);
    }
  }

  private buildVersionPayload(workflowId: number): any {
    const trigger = this.selectedTrigger;
    if (!trigger?.workflowTriggerId) {return null;}

    const inputValues = this.gatherInputValues();
    const cleanedInputValues = {};
    for (const [key, value] of Object.entries(inputValues)) {
      if (key.endsWith('-radio')) {continue;}
      if (value === undefined || value === '') {continue;}
      cleanedInputValues[key] = value;
    }

    const payload: any = {
      workflow_id: workflowId,
      trigger_data: {
        id: trigger.workflowTriggerId,
        name: trigger.title,
        description: trigger.description || '',
      },
      actions_data: this.buildActionsPayload(),
    };

    if (Object.keys(cleanedInputValues).length > 0) {
      payload.trigger_data.input_values = cleanedInputValues;
    }

    // Preserve the trigger's step_id from the last saved version so the
    // backend can resolve {{N.field}} references. See edit-workflow.ts:623-628.
    const savedTrigger = this.latestVersion?.workflow_version_trigger;
    if (savedTrigger && payload.trigger_data.id === savedTrigger.trigger_id) {
      payload.trigger_data.step_id = savedTrigger.step_id;
    }

    return payload;
  }

  private hasValidActions(): boolean {
    return this.selectedActions.some(action => action !== null);
  }

  private normalizeMultiSelectValue(value: any, inputType: string | undefined): any {
    if (inputType !== 'multi_select') {return value;}
    if (value === '' || value === undefined) {return null;}
    if (Array.isArray(value) && value.length === 0) {return null;}
    return value;
  }

  private buildActionsPayload(): any[] {
    const actions = [];
    this.selectedActions.forEach((action, index) => {
      if (!action) {return;}
      const inputValues = {};
      const form = this.actionForms[index];
      const formId = this.actionFormIds[index];
      if (form) {
        Object.keys(form.controls).forEach(key => {
          const chipTextArea = this.chipTextAreaComponents?.find(
            c => c.id === formId && c.inputKey === key
          );
          if (chipTextArea) {
            inputValues[key] = chipTextArea.innerHtml || '';
          } else {
            const value = form.get(key)?.value;
            const inputMeta = action.inputs?.find(i => i.key === key);
            const normalized = this.normalizeMultiSelectValue(value, inputMeta?.form?.type);
            if (normalized !== undefined) {
              inputValues[key] = normalized;
            }
          }
        });
      }
      const actionPayload: any = { id: action.id, key: action.key, input_values: inputValues };
      // Preserve the action's step_id from the last saved version so the
      // backend can resolve {{N.field}} references. See edit-workflow.ts:735-741.
      const savedAction = this.latestVersion?.workflow_version_actions?.[index];
      if (savedAction && savedAction.action_id === action.id) {
        actionPayload.step_id = savedAction.step_id;
      }
      actions.push(actionPayload);
    });
    return actions;
  }

  async removeWorkflowConfig(config: WorkflowConfig) {
    try {
      if (config.id) {
        await this.workflowsProvider.deleteWorkflow(Number(config.id));
      }
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
      return;
    }

    this.allWorkflows = this.allWorkflows.filter(w => String(w.id) !== config.id);
    if (this.selectedTrigger) {
      this.selectedTrigger.workflowIds = this.selectedTrigger.workflowIds.filter(id => String(id) !== config.id);
    }

    this.workflowConfigs = this.workflowConfigs.filter(c => c.id !== config.id);
    if (this.activeWorkflowConfigId === config.id) {
      this.activeWorkflowConfigId = null;
      this.activeWorkflowData = null;
      this.activeWorkflowLatestVersionId = null;
      this.latestVersion = null;
      this.workflowNameForm.reset();
      this.form.patchValue({ instructions: '', blockedActions: [] });
      if (this.workflowConfigs.length > 0) {
        await this.selectWorkflowConfig(this.workflowConfigs[0]);
      } else {
        this.clearActivityFilter();
      }
    }
    this.updateVisibleWorkflowConfigs();
  }

  showDeleteConfirm = false;

  removeActiveWorkflowConfig() {
    this.showDeleteConfirm = true;
  }

  cancelDelete() {
    this.showDeleteConfirm = false;
  }

  async confirmDeleteWorkflow() {
    const config = this.workflowConfigs.find(c => c.id === this.activeWorkflowConfigId);
    if (!config) {return;}
    this.showDeleteConfirm = false;
    await this.removeWorkflowConfig(config);
    this.isViewingPublished = false;
  }

  loadActionTypes(actionTypes: any[]) {
    const actionResult = actionTypes.reduce((acc, obj) => {
      const key = obj['category'];
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});
    this.allActions = Object.keys(actionResult).map(key => ({ category: key, data: actionResult[key] }));
    this.allActionsData = [];
    this.allActions.forEach(group => {
      group.data.forEach(data => {
        if (data.key === 'concierge_task.create') {
          data.name = 'Create Concierge Task';
          data.description = 'Prompt our concierge to perform an action';
          data.icon_url = 'assets/img/concierge.svg';
          (data.inputs || []).forEach(input => {
            if (input.form?.label === 'Task Description') {
              input.form.label = 'Instructions';
            }
          });
        }
        this.allActionsData.push(data);
      });
    });
    this.filteredActions = this.allActions;
  }

  searchActions(value: string) {
    if (!value || value === '') {
      this.filteredActions = this.allActions;
      return;
    }
    const valueLower = value.toLowerCase();
    this.filteredActions = this.allActionsData.filter(action =>
      action.name?.toLowerCase().includes(valueLower) ||
      action.description?.toLowerCase().includes(valueLower)
    );
  }

  async selectActionType(action: any, actionIndex: number) {
    try {
      const inputData = await this.workflowsProvider.getInputData(action.id, 'Action');
      action.inputData = inputData || [];
      action.inputs?.forEach(input => {
        input.options = this.getActionSelectOptions(input, inputData);
      });
      const newFormId = Math.random().toString(36).substring(7);
      const newForm = this.fb.group({});
      action.inputs?.forEach(input => {
        newForm.addControl(input.key, this.fb.control(''));
      });
      this.actionFormIds[actionIndex] = newFormId;
      this.actionForms[actionIndex] = newForm;
      this.selectedChipTextAreaId = null;
      this.buildOutputsForAction(action, actionIndex);
      this.selectedActions[actionIndex] = action;
      const actionSub = newForm.valueChanges
        .subscribe(() => this.updateAgent());
      this.formSubscriptions.push(actionSub);
      this.updateAgent();
    } catch (error) {
      this.util.showError((error.error && error.error.message) ? error.error.message : error.message, 10000);
    }
  }

  addAction(): void {
    const lastAction = this.selectedActions[this.selectedActions.length - 1];
    if (lastAction?.key === 'concierge_task.create') {
      this.util.showError('You cannot add another action after an AI task');
      return;
    }
    this.selectedActions.push(null);
    this.actionForms.push(this.fb.group({}));
    this.actionFormIds.push('');
  }

  removeAction(actionIndex: number) {
    this.selectedActions.splice(actionIndex, 1);
    this.actionForms.splice(actionIndex, 1);
    this.actionFormIds.splice(actionIndex, 1);
    if (this.selectedActions.length === 0) {
      this.addAction();
    }
    this.actionSearchForm.patchValue({ search: '' });
    this.filteredActions = this.allActions;

    if (!this.hasValidActions() && this.isAutomationEnabled && this.activeWorkflowConfigId) {
      this.isAutomationEnabled = false;
      const config = this.workflowConfigs.find(c => c.id === this.activeWorkflowConfigId);
      if (config) {config.enabled = false;}
      this.workflowsProvider.pauseWorkflow(Number(this.activeWorkflowConfigId)).catch(() => { /* ignore */ });
      const wf = this.allWorkflows.find(w => String(w.id) === this.activeWorkflowConfigId);
      if (wf) {wf.paused_at = new Date().toISOString();}
    }
    this.updateAgent();
  }

  getActionSelectOptions(input: any, inputData: any[]) {
    if (input?.options) {
      return input.options || [];
    }
    const options = inputData?.find(data => data?.input_key === input?.key)?.input_options;
    return options?.map(option => ({
      ...option,
      value: option.value,
      viewValue: option.label
    })) || [];
  }

  hideInput(action: any, input: any): boolean {
    if (action?.key === 'concierge_task.create' && input.key === 'subject') {
      return true;
    }
    const inputValue = action?.input_values?.[input.key];
    if (inputValue === '{{hidden_input}}') {
      return true;
    }
    return this.getInputDefaultValue(action, input) === '{{hidden_input}}';
  }

  getInputDefaultValue(action: any, input: any): string {
    const inputData = action?.inputData?.find(item => item?.input_key === input.key);
    return inputData?.input_default_value || '';
  }

  async onOptionChange(action: any, actionIndex: number, input: any): Promise<void> {
    if (input?.key === 'receiver_type') {
      await this.handleReceiverTypeChange(action, actionIndex);
    }
  }

  private async handleReceiverTypeChange(action: any, actionIndex: number): Promise<void> {
    const form = this.actionForms[actionIndex];
    if (!form || !action) {
      return;
    }
    const receiverType = form.get('receiver_type')?.value || '';
    const inputData = await this.workflowsProvider.getInputData(
      action.id,
      'Action',
      JSON.stringify({ receiver_type: receiverType })
    );
    action.inputData = inputData || [];

    // Refresh select/multi_select options for every input from the new fetch
    // so the multi_select dropdowns (e.g. to_customer_members) populate when
    // receiver_type becomes customer_member.
    action.inputs?.forEach(actionInput => {
      const inputDataItem = inputData?.find(d => d?.input_key === actionInput?.key);
      if (inputDataItem) {
        actionInput.options = (inputDataItem.input_options || []).map(option => ({
          ...option,
          value: option.value,
          viewValue: option.label,
        }));
      }
    });

    // Translate the backend's string-typed `input_default_value` into a typed
    // value for array-typed inputs ('' / sentinel → []) so the multi_select
    // form control persists the right shape on save.
    const toCustomerMembersInput = inputData?.find(d => d?.input_key === 'to_customer_members');
    if (toCustomerMembersInput) {
      const defaultValue = toCustomerMembersInput.input_default_value;
      const newValue = (defaultValue === '' || defaultValue === '{{hidden_input}}') ? [] : defaultValue;
      form.get('to_customer_members')?.patchValue(newValue);
    }
  }

  setSelectedChipTextArea(inputKey: string) {
    this.selectedChipTextAreaId = inputKey;
  }

  toggleShowOutputOptions(actionIndex: number, outputIndex: number) {
    if (this.selectedActions[actionIndex]?.outputs?.[outputIndex]) {
      this.selectedActions[actionIndex].outputs[outputIndex].showOptions = !this.selectedActions[actionIndex].outputs[outputIndex].showOptions;
    }
  }

  addChipPlaceholder(item: any, formId: string, inputKey: string): void {
    const chipTextArea = this.chipTextAreaComponents?.find(
      c => c.id === formId && c.inputKey === inputKey
    );
    if (chipTextArea) {
      const placeholder = {
        text: item.plain_title || item.title,
        value: item.key
      };
      chipTextArea.appendPlaceholder(placeholder);
    }
  }

  onInputChange(_event: any) {
    this.updateAgent();
  }

  resetActions(): void {
    this.selectedActions = [null];
    this.actionForms = [this.fb.group({})];
    this.actionFormIds = [''];
    this.selectedChipTextAreaId = null;
    this.actionSearchForm.patchValue({ search: '' });
    this.filteredActions = this.allActions;
  }

  buildOutputsForAction(action: any, actionIndex?: number): void {
    action.outputs = [];
    const outputVars = this.selectedTrigger?.triggerOutputVariables ?? [];
    const triggerOutputs = this.removeSomeTextFromResponse([...outputVars.map(o => ({...o}))]);
    action.outputs.push({
      array: triggerOutputs,
      title: `<i>Add outputs from the <u>${this.selectedTrigger?.title || 'trigger'}</u> trigger</i>`,
      showOptions: false
    });

    const idx = actionIndex ?? this.selectedActions.indexOf(action);
    if (idx > 0) {
      for (let i = 0; i < idx; i++) {
        const prior = this.selectedActions[i];
        const priorOutputs = prior?.output_variables ?? prior?.outputs_schema ?? [];
        if (priorOutputs.length > 0) {
          action.outputs.push({
            array: this.removeSomeTextFromResponse([...priorOutputs.map(o => ({...o}))]),
            title: `<i>Add outputs from the <u>${prior.name}</u> action</i>`,
            showOptions: false
          });
        }
      }
    }
  }

  private refreshOutputsFromVersion(version: any): void {
    if (!version) {return;}
    const versionTriggerOutputs = version?.workflow_version_trigger?.output_variables;
    if (this.selectedTrigger && versionTriggerOutputs?.length) {
      this.selectedTrigger.triggerOutputVariables = versionTriggerOutputs;
    }
    const versionActions = version?.workflow_version_actions ?? [];
    versionActions.forEach((savedAction: any, index: number) => {
      const action = this.selectedActions[index];
      if (action && savedAction?.output_variables?.length) {
        action.output_variables = savedAction.output_variables;
      }
    });
    // Rebuild each action's output dropdown groups with the refreshed keys.
    this.selectedActions.forEach((action: any, idx: number) => {
      if (action) {
        this.buildOutputsForAction(action, idx);
      }
    });
  }

  rewriteContentToCurrentKeys(content: string, baseChips: ChipPlaceholder[]): string {
    if (!content) {return content;}
    const stripPrefix = (key: string) => key.replace(/^\{+|\}+$/g, '').replace(/^\d+\./, '');
    return content.replace(/\{\{[^}]+\}\}/g, (match) => {
      const fieldPart = stripPrefix(match);
      const current = baseChips.find(c => stripPrefix(c.value) === fieldPart);
      return current ? current.value : match;
    });
  }

  chipsFromContent(content: string, baseChips: ChipPlaceholder[]): ChipPlaceholder[] {
    const result = [...baseChips];
    const seen = new Set(baseChips.map(c => c.value));
    const matches = content?.match(/\{\{[^}]+\}\}/g) ?? [];
    // Strip numeric step prefixes (e.g. "3.job.id" -> "job.id") so saved chips
    // match even when the draft's step id differs from the current one.
    const stripPrefix = (key: string) => key.replace(/^\{+|\}+$/g, '').replace(/^\d+\./, '');
    matches.forEach(match => {
      if (seen.has(match)) {return;}
      seen.add(match);
      const strippedMatch = stripPrefix(match);
      const friendly = baseChips.find(c =>
        stripPrefix(c.value) === strippedMatch
      );
      result.push({
        text: friendly?.text || strippedMatch,
        value: match
      });
    });
    return result;
  }

  buildChipsForAction(_action: any): ChipPlaceholder[] {
    const chips: ChipPlaceholder[] = [];
    const seen = new Set<string>();
    const addChip = (text: string, value: string) => {
      if (!value || seen.has(value)) {return;}
      seen.add(value);
      chips.push({ text, value });
    };
    const addOutputs = (outputs: any[]) => {
      outputs?.forEach((item: any) => {
        if (!item?.key) {return;}
        const text = item.plain_title || item.title;
        // Register the key as-is plus both wrapped/unwrapped variants so the
        // chip-text-area's {{...}} matcher can find it regardless of format.
        const raw = item.key.replace(/^\{+|\}+$/g, '');
        addChip(text, item.key);
        addChip(text, `{{${raw}}}`);
      });
    };
    addOutputs(this.selectedTrigger?.triggerOutputVariables ?? []);
    this.selectedActions.forEach((priorAction: any) => {
      addOutputs(priorAction?.output_variables ?? []);
    });
    return chips;
  }

  removeSomeTextFromResponse(outputs: any[]): any[] {
    outputs.forEach(output => {
      if (output.title?.includes('Event')) {
        output.title = output.title.replace('Event.', '');
      }
      if (output.title?.includes('Model: Job')) {
        output.title = output.title.replace('Model: Job.', '');
      }
      if (output.title?.includes('object')) {
        output.title = null;
      }
    });
    return outputs.filter(output => output.title !== null);
  }

  private gatherInputValues(): any {
    const values: any = {};
    const specialFields = this.specialTriggerFields[this.selectedTrigger?.key];
    if (specialFields) {
      specialFields.forEach(field => {
        values[field] = this.form.value[field];
      });
    }
    const dynamicInputs = this.selectedTrigger?.dynamicInputs || [];
    for (const input of dynamicInputs) {
      if (specialFields?.includes(input.key)) {continue;}
      const radioKey = `${input.key}-radio`;
      const radioControl = this.form.get(radioKey);
      if (radioControl) {
        const radioValue = radioControl.value;
        if (radioValue === '' || radioValue === undefined) {
          values[input.key] = null;
        } else {
          values[input.key] = this.form.value[input.key];
        }
      } else {
        values[input.key] = this.normalizeMultiSelectValue(
          this.form.value[input.key],
          input.form?.type,
        );
      }
    }
    return values;
  }

  getTriggerIcon(category: string): string {
    const iconMap: { [key: string]: string } = {
      'Jobs': 'assets/svg/checkmark-circle.svg',
      'Scheduled': 'assets/svg/calendar-outline.svg',
      'Invoices': 'assets/svg/cash-outline.svg',
      'Pros': 'assets/svg/person-outline.svg',
      'Messages': 'assets/svg/chatbox-ellipses-outline.svg',
      'Communication': 'assets/svg/chatbox-ellipses-outline.svg',
      'Automation': 'assets/svg/settings-outline.svg',
      'Tasks': 'assets/svg/checkmark-circle.svg',
      'Guest Reservations': 'assets/svg/calendar-outline.svg',
      'Address': 'assets/svg/home-outline.svg',
    };
    return iconMap[category] || 'assets/svg/settings-outline.svg';
  }

  getTriggerIconColor(category: string): string {
    const colorMap: { [key: string]: string } = {
      'Jobs': 'linear-gradient(135deg, #9cbfe6, #6b9fd4)',
      'Scheduled': 'linear-gradient(135deg, #f99188, #e87066)',
      'Invoices': 'linear-gradient(135deg, #95d6a4, #6bc47e)',
      'Pros': 'linear-gradient(135deg, #c4a6e8, #a07ad0)',
      'Messages': 'linear-gradient(135deg, #78c9e4, #4fb0d0)',
      'Communication': 'linear-gradient(135deg, #78c9e4, #4fb0d0)',
      'Automation': 'linear-gradient(135deg, #999, #777)',
      'Tasks': 'linear-gradient(135deg, #9cbfe6, #6b9fd4)',
      'Guest Reservations': 'linear-gradient(135deg, #f99188, #e87066)',
      'Address': 'linear-gradient(135deg, #95d6a4, #6bc47e)',
    };
    return colorMap[category] || 'linear-gradient(135deg, #999, #777)';
  }

  // Field builder methods
  buildTimeOfDayItems(): any[] {
    const items = [];
    for (let hour = 0; hour < 24; hour++) {
      const hour12 = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);
      const ampm = hour < 12 ? 'AM' : 'PM';
      const hourStr = hour.toString().padStart(2, '0');
      items.push({ viewValue: `${hour12}:00 ${ampm}`, value: `${hourStr}:00` });
      items.push({ viewValue: `${hour12}:30 ${ampm}`, value: `${hourStr}:30` });
    }
    return items;
  }

  buildDateOfTheMonthItems(): any[] {
    const items = [];
    for (let day = 1; day <= 31; day++) {
      const suffix = this.getOrdinalSuffix(day);
      items.push({ viewValue: `${day}${suffix}`, value: day });
    }
    return items;
  }

  buildTimeValueItems(): any[] {
    const items = [];
    for (let i = 1; i <= 60; i++) {
      items.push({ viewValue: i.toString(), value: i });
    }
    return items;
  }

  getOrdinalSuffix(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  }

  getDefaultValueForField(field: string): any {
    const defaults: { [key: string]: any } = {
      timezone: 'US/Eastern',
      trigger_on_weekends: 'yes',
      time_of_day: '09:00',
      day_of_the_week: 'monday',
      date_of_the_month: 1,
      past_from_start_value: 1,
      past_from_start_unit: 'hours',
      past_from_end_value: 1,
      past_from_end_unit: 'hours',
      start_within_value: 24,
      start_within_unit: 'hours',
      checkin_within_value: 24,
      checkin_within_unit: 'hours',
      due_date_within_value: 1,
      due_date_within_direction: 'before'
    };
    return defaults[field] ?? '';
  }

  async toggleAutomationEnabled() {
    this.isAutomationEnabled = !this.isAutomationEnabled;
    const config = this.workflowConfigs.find(c => c.id === this.activeWorkflowConfigId);
    if (config) {
      config.enabled = this.isAutomationEnabled;
    }

    if (this.activeWorkflowConfigId) {
      try {
        const workflowId = Number(this.activeWorkflowConfigId);
        if (this.isAutomationEnabled) {
          await this.workflowsProvider.unpauseWorkflow(workflowId);
        } else {
          await this.workflowsProvider.pauseWorkflow(workflowId);
        }
        const wf = this.allWorkflows.find(w => String(w.id) === this.activeWorkflowConfigId);
        if (wf) {
          wf.paused_at = this.isAutomationEnabled ? null : new Date().toISOString();
        }
      } catch (error) {
        this.isAutomationEnabled = !this.isAutomationEnabled;
        if (config) {config.enabled = this.isAutomationEnabled;}
        this.util.showError((error.error?.message) || error.message, 10000);
      }
    }
  }

  async publishWorkflow() {
    if (!this.activeWorkflowConfigId || this.isPublishing) {return;}

    this.isPublishing = true;
    try {
      const workflowId = Number(this.activeWorkflowConfigId);
      const versionPayload = this.buildVersionPayload(workflowId);
      if (versionPayload) {
        const newVersion = await this.workflowsProvider.createWorkflowVersion(versionPayload);
        await this.workflowsProvider.publishWorkflow(newVersion.id);
        this.activeWorkflowLatestVersionId = newVersion.id;
        this.latestVersion = newVersion;
        await this.refreshWorkflowInAllWorkflows(workflowId);
        await this.loadPublishedView(this.activeWorkflowData);
      }
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.isPublishing = false;
    }
  }

  async loadPublishedView(workflow: any) {
    this.activeWorkflowData = workflow;
    this.isAutomationEnabled = !workflow.paused_at;
    this.workflowNameForm.patchValue({ workflowName: workflow.name }, { emitEvent: false });
    this.patchTagsForm(workflow?.tags);
    this.publishedVersionIsLatestVersion = workflow.draft_version
      ? (workflow.draft_version.id <= workflow.published_version.id)
      : true;

    try {
      const versions = await this.workflowsProvider.getWorkflowVersion(workflow.id);
      const publishedVersion = versions?.find(v => v.id === workflow.published_version.id) || versions?.[0];
      if (!publishedVersion) {return;}

      const trigger = publishedVersion.workflow_version_trigger;
      if (trigger) {
        const triggerType = (this.workflowTriggerTypes || []).find(wt => wt.id === trigger.trigger_id);
        const inputs = triggerType?.inputs || [];
        this.publishedTriggerDisplay = this.buildTriggerDisplayValues(inputs, trigger.input_values || {});
      } else {
        this.publishedTriggerDisplay = [];
      }

      const versionActions = publishedVersion.workflow_version_actions || [];
      // Build a flat chips list from the published version's trigger + all
      // action output_variables so {{N.field}} placeholders in readonly chip
      // text areas render as labeled chips instead of raw squiggly-brace text.
      const publishedChips = this.buildChipsFromVersion(publishedVersion);
      this.publishedActionsDisplay = versionActions.map((savedAction, index) => {
        const actionType = this.allActionsData.find(a => a.id === savedAction.action_id);
        const formId = `published-${index}-${savedAction.id ?? savedAction.step_id ?? index}`;
        const displayInputs = (actionType?.inputs || [])
          .filter(input => {
            if (input.form?.type === 'filter') {return false;}
            if (savedAction.input_values?.[input.key] === undefined) {return false;}
            if (savedAction.input_values[input.key] === '{{hidden_input}}') {return false;}
            if (actionType?.key === 'concierge_task.create' &&
                (input.key === 'subject' || input.key === 'attachments' || input.form?.type === 'files')) {
              return false;
            }
            return true;
          })
          .map(input => ({
            key: input.key,
            label: input.form?.label || input.key,
            content: this.formatActionInputValue(savedAction.input_values[input.key]),
            chips: publishedChips,
          }));
        return {
          name: actionType?.name || 'Unknown Action',
          icon_url: actionType?.icon_url || '',
          description: actionType?.description || '',
          formId,
          displayInputs,
        };
      });

      // Populate each readonly chip-text-area once Angular has rendered the view.
      setTimeout(() => {
        this.publishedActionsDisplay.forEach(action => {
          const chipTextAreas = this.chipTextAreaComponents?.filter(c => c.id === action.formId) ?? [];
          chipTextAreas.forEach(chipTextArea => {
            const input = action.displayInputs.find(i => i.key === chipTextArea.inputKey);
            if (!input) {return;}
            chipTextArea.readonly = true;
            chipTextArea.body = { content: input.content, chips: input.chips };
            chipTextArea.parseAndSetBody();
          });
        });
      }, 500);
    } catch (error) {
      this.publishedTriggerDisplay = [];
      this.publishedActionsDisplay = [];
    }

    this.isViewingPublished = true;
  }

  private buildChipsFromVersion(version: any): ChipPlaceholder[] {
    const chips: ChipPlaceholder[] = [];
    const seen = new Set<string>();
    const addOutputs = (outputs: any[]) => {
      outputs?.forEach((item: any) => {
        if (!item?.key || seen.has(item.key)) {return;}
        seen.add(item.key);
        chips.push({
          text: item.plain_title || item.title,
          value: item.key
        });
      });
    };
    addOutputs(version?.workflow_version_trigger?.output_variables ?? []);
    (version?.workflow_version_actions ?? []).forEach((act: any) => {
      addOutputs(act?.output_variables ?? []);
    });
    return chips;
  }

  private buildTriggerDisplayValues(inputs: any[], inputValues: any): any[] {
    return inputs.map(input => {
      const value = inputValues[input.key];
      let displayValue: string;
      if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
        displayValue = `All ${this.getFilterNameInSingular(input.form?.label)}`;
      } else if (input.key === 'assigned_to_concierge') {
        displayValue = value === true ? 'Assigned to Concierge' : value === false ? 'Not Assigned to Concierge' : 'No filter';
      } else if (Array.isArray(value)) {
        const dynamicInput = this.selectedTrigger?.dynamicInputs?.find(d => d.key === input.key);
        const names = value.map(val => {
          const item = dynamicInput?.items?.find(itm => itm.value === val);
          return item?.viewValue || val;
        });
        displayValue = names.join(', ') || 'Selected items';
      } else {
        displayValue = String(value);
      }
      return { key: input.key, form: input.form, displayValue };
    }).filter(item => item.form);
  }

  private formatActionInputValue(value: any): string {
    if (value === undefined || value === null) {return '';}
    if (Array.isArray(value)) {return value.join(', ');}
    return String(value);
  }

  async goToEditMode() {
    this.isViewingPublished = false;
    const config = this.workflowConfigs.find(c => c.id === this.activeWorkflowConfigId);
    if (config) {
      this.isFormInitializing = true;
      this.isAutomationEnabled = config.enabled !== false;
      this.resetActions();
      this.workflowNameForm.patchValue({ workflowName: config.name }, { emitEvent: false });
      const wf = this.allWorkflows.find(w => String(w.id) === config.id);
      this.patchTagsForm(wf?.tags);
      this.form.patchValue({
        instructions: config.instructions,
        blockedActions: config.blockActions
      }, { emitEvent: false });
      const specialFields = this.specialTriggerFields[this.selectedTrigger?.key];
      if (specialFields && config.inputValues) {
        specialFields.forEach(field => {
          if (this.form.get(field) && config.inputValues[field] !== undefined) {
            this.form.get(field).setValue(config.inputValues[field], { emitEvent: false });
          }
        });
      }
      if (this.selectedTrigger?.dynamicInputs?.length && config.inputValues) {
        this.selectedTrigger.dynamicInputs.forEach(input => {
          if (specialFields?.includes(input.key)) {return;}
          if (this.form.get(input.key) && config.inputValues[input.key] !== undefined) {
            const value = config.inputValues[input.key];
            this.form.get(input.key)?.setValue(value, { emitEvent: false });
            const radioKey = `${input.key}-radio`;
            if (this.form.get(radioKey)) {
              const isAll = value === null || value === '' || (Array.isArray(value) && value.length === 0);
              this.form.get(radioKey).setValue(isAll ? '' : this.getAdjustedLabel((input.form?.label || '').toLowerCase()), { emitEvent: false });
            }
          }
        });
      }
      await this.loadActionsForWorkflow(config.id);
      this.isFormInitializing = false;
    }
  }

  async goToPublishedView() {
    if (this.activeWorkflowData?.published_version) {
      const workflowId = Number(this.activeWorkflowConfigId);
      await this.refreshWorkflowInAllWorkflows(workflowId);
      await this.loadPublishedView(this.activeWorkflowData);
    }
  }

  private async refreshWorkflowInAllWorkflows(workflowId: number) {
    try {
      const updated = await this.workflowsProvider.getWorkflowWithVersions(workflowId);
      const index = this.allWorkflows.findIndex(w => w.id === workflowId);
      if (index >= 0) {
        this.allWorkflows[index] = updated;
      }
      this.activeWorkflowData = updated;
      const config = this.workflowConfigs.find(c => c.id === String(workflowId));
      if (config) {
        config.enabled = !updated.paused_at;
      }
    } catch (error) {
      // Silently fail
    }
  }

  private buildTooltipLinesForConfigs() {
    const specialFields = this.specialTriggerFields[this.selectedTrigger?.key];
    this.workflowConfigs.forEach(config => {
      config.tooltipLines = [];
      if (specialFields) {
        specialFields.forEach(field => {
          const value = config.inputValues?.[field] ?? this.getDefaultValueForField(field);
          if (value !== undefined && value !== null && value !== '') {
            config.tooltipLines.push({
              label: this.getFieldLabel(field),
              value: this.formatFieldValue(value)
            });
          }
        });
      }
      if (this.selectedTrigger?.dynamicInputs?.length) {
        this.selectedTrigger.dynamicInputs.forEach(input => {
          if (specialFields?.includes(input.key)) {return;}
          const value = config.inputValues?.[input.key];
          if (value !== undefined && value !== null && value !== '' &&
              !(Array.isArray(value) && value.length === 0)) {
            config.tooltipLines.push({
              label: input.form?.label || input.key,
              value: Array.isArray(value) ? value.join(', ') : this.formatFieldValue(value)
            });
          }
        });
      }
    });
  }

  private getFieldLabel(field: string): string {
    const labels: { [key: string]: string } = {
      timezone: 'Timezone',
      trigger_on_weekends: 'Trigger on weekends',
      time_of_day: 'Time',
      day_of_the_week: 'Day of week',
      date_of_the_month: 'Day of month',
      past_from_start_value: 'How far past job start',
      past_from_start_unit: 'Unit',
      past_from_end_value: 'How far past job end',
      past_from_end_unit: 'Unit',
      start_within_value: 'Job starts within',
      start_within_unit: 'Unit',
      checkin_within_value: 'Check-in within',
      checkin_within_unit: 'Unit',
      due_date_within_value: 'Due date within',
      due_date_within_direction: 'Before or after'
    };
    return labels[field] || field;
  }

  private formatFieldValue(value: any): string {
    if (typeof value === 'string') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    return String(value);
  }

  // --- Dynamic Trigger Filter Methods ---

  shouldShowTriggerFilters(): boolean {
    return !!this.specialTriggerFields[this.selectedTrigger?.key] || this.hasDynamicInputs();
  }

  hasDynamicInputs(): boolean {
    return this.selectedTrigger?.dynamicInputs?.some(
      input => input.form?.type === 'select' || input.form?.type === 'multi_select' ||
               input.form?.type === 'number' || input.form?.type === 'text' ||
               input.key === 'assigned_to_concierge'
    ) || false;
  }

  shouldDisplayTriggerFiltersText(): boolean {
    return this.dynamicInputsLoaded &&
      !!this.selectedTrigger?.dynamicInputs?.length &&
      this.selectedTrigger.dynamicInputs.some(
        (input) => input?.form?.type === 'multi_select' || input?.form?.type === 'select'
      );
  }

  getTriggerFilterHeaderText(): string {
    if (this.selectedTrigger?.dynamicInputs?.some(
      (input) => input?.form?.type === 'multi_select' || input?.form?.type === 'select'
    )) {
      return 'These can narrow when a trigger fires.';
    }
    if (this.selectedTrigger?.title?.includes('Every')) {
      return 'Run this trigger on:';
    } else if (this.selectedTrigger?.title === 'Job Completed') {
      return 'Filter jobs completed by:';
    }
    return 'Filter this trigger by:';
  }

  async getDynamicInputs(): Promise<void> {
    const trigger = this.selectedTrigger;
    if (!trigger?.dynamicInputs?.length || !trigger.workflowTriggerId) {
      this.dynamicInputsLoaded = true;
      return;
    }

    const hasSelect = trigger.dynamicInputs.some(
      input => input.form?.type === 'select' || input.form?.type === 'multi_select' || input.key === 'assigned_to_concierge'
    );

    if (!hasSelect) {
      this.dynamicInputsLoaded = true;
      return;
    }

    try {
      const inputData = await this.workflowsProvider.getInputData(trigger.workflowTriggerId, 'Trigger');
      inputData.forEach(data => {
        const input = trigger.dynamicInputs.find(i => i.key === data.input_key);
        if (!input) {return;}

        input.items = data.input_options.map(option => ({
          value: option.value,
          viewValue: option.label
        }));

        const allOptionsLabel = this.getAllOptionsLabel(input.form?.label);
        this.addRadioItems(input.form?.label, allOptionsLabel);

        if (!input.required && input.form?.type !== 'multi_select' && input.key !== 'assigned_to_concierge') {
          input.items.unshift({ value: '', viewValue: allOptionsLabel });
        }
      });
    } catch (error) {
      // Silently fail — filters are optional enhancement
    }

    this.dynamicInputsLoaded = true;
  }

  getAllOptionsLabel(label: string): string {
    const map = {
      'Categories': 'Trigger on all categories',
      'Addresses': 'Trigger on all properties',
      'Address': 'Trigger on all properties',
      'Service Types': 'Trigger on all services',
      'Service Type': 'Trigger on all services',
      'Issue Type': 'Trigger on all issues',
      'Job State': 'Trigger on all job states',
      'Assigned to Concierge?': 'No filter',
      'Only trigger on scheduled or in progress jobs': 'No filter',
    };
    return map[label] ?? `All ${label}`;
  }

  addRadioItems(label: string, allOptionsLabel: string): void {
    if (this.radioItems?.[label]) {return;}
    const adjustedLabel = this.getAdjustedLabel((label || '').toLowerCase());
    this.radioItems = {
      ...this.radioItems,
      [label]: [
        { value: '', viewValue: allOptionsLabel },
        { value: adjustedLabel, viewValue: adjustedLabel }
      ]
    };
  }

  getAdjustedLabel(label: string): string {
    let adjustedLabel = `Trigger only on some ${label}`;
    if (label.includes('address')) {
      adjustedLabel = 'Trigger only on some properties';
    } else if (label.includes('state')) {
      adjustedLabel = 'Trigger on specific states';
    } else if (label.includes('assigned to concierge?')) {
      adjustedLabel = 'Trigger only for concierge assigned or not';
    }
    return adjustedLabel;
  }

  getFilterNameInSingular(label: string): string {
    if (!label) {return '';}
    const filterNames = {
      'Categories': 'Category',
      'Addresses': 'Property',
      'Address': 'Property',
      'Service Types': 'Service',
      'Service Type': 'Service',
      'Issue Type': 'Maintenance Task',
      'Assigned to Concierge?': 'Concierge',
    };
    return filterNames[label] || label || '';
  }

  onRadioOptionChange(event: any, inputKey: string): void {
    if (event.value === '') {
      this.form.get(inputKey)?.reset();
    }
  }

  // --- Auto-Schedule Methods ---

  async loadAutoScheduleData() {
    this.autoScheduleLoading = true;
    this.autoScheduleSearchForm.patchValue({ search: '' });
    this.showAllAutoScheduleChips = false;
    try {
      const addresses = await this.client.getAddresses();
      this.autoScheduleAddresses = addresses;
      this.filteredAutoScheduleAddresses = [...addresses];
      this.updateVisibleAddresses();
      if (this.autoScheduleAddresses.length > 0) {
        await this.selectAutoScheduleAddress(this.autoScheduleAddresses[0]);
      }
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.autoScheduleLoading = false;
    }
  }

  filterAutoScheduleAddresses() {
    const search = (this.autoScheduleSearchForm.get('search')?.value || '').toLowerCase();
    if (!search) {
      this.filteredAutoScheduleAddresses = [...this.autoScheduleAddresses];
    } else {
      this.filteredAutoScheduleAddresses = this.autoScheduleAddresses.filter(a => {
        const name = (a.address_name || a.address1 || '').toLowerCase();
        return name.includes(search);
      });
    }
    this.showAllAutoScheduleChips = false;
    this.updateVisibleAddresses();
  }

  toggleShowAllAutoScheduleChips() {
    this.showAllAutoScheduleChips = !this.showAllAutoScheduleChips;
    this.updateVisibleAddresses();
  }

  isMeasuringChips = false;

  updateVisibleAddresses() {
    if (this.showAllAutoScheduleChips) {
      this.visibleAutoScheduleAddresses = [...this.filteredAutoScheduleAddresses];
      this.isMeasuringChips = false;
      return;
    }
    this.visibleAutoScheduleAddresses = [...this.filteredAutoScheduleAddresses];
    this.isMeasuringChips = true;
    setTimeout(() => {
      setTimeout(() => {
        this.calculateChipLimit();
        this.isMeasuringChips = false;
      });
    });
  }

  private calculateChipLimit() {
    if (!this.workflowChipsContainerRef?.nativeElement) {
      return;
    }
    const container = this.workflowChipsContainerRef.nativeElement;
    const chips = container.querySelectorAll('ion-chip');
    if (chips.length === 0) {
      return;
    }

    // Group chips by row (same offsetTop)
    const rows: number[][] = [];
    let currentRowTop = -1;
    chips.forEach((chip: HTMLElement, index: number) => {
      const top = chip.offsetTop;
      if (top !== currentRowTop) {
        rows.push([]);
        currentRowTop = top;
      }
      rows[rows.length - 1].push(index);
    });

    // If 3 rows or fewer, show all
    if (rows.length <= 3) {
      this.autoScheduleChipLimit = this.filteredAutoScheduleAddresses.length;
      this.visibleAutoScheduleAddresses = [...this.filteredAutoScheduleAddresses];
      return;
    }

    // Take chips from first 3 rows, minus 1 to leave room for "View More"
    const chipsInThreeRows = rows[0].length + rows[1].length + rows[2].length;
    this.autoScheduleChipLimit = chipsInThreeRows - 1;
    this.visibleAutoScheduleAddresses = this.filteredAutoScheduleAddresses.slice(0, this.autoScheduleChipLimit);
  }

  toggleShowAllWorkflowChips() {
    this.showAllWorkflowChips = !this.showAllWorkflowChips;
    this.updateVisibleWorkflowConfigs();
  }

  isMeasuringWorkflowChips = false;

  updateVisibleWorkflowConfigs() {
    if (this.showAllWorkflowChips) {
      this.visibleWorkflowConfigs = [...this.workflowConfigs];
      return;
    }
    this.visibleWorkflowConfigs = [...this.workflowConfigs];
    this.isMeasuringWorkflowChips = true;
    setTimeout(() => {
      setTimeout(() => {
        this.calculateWorkflowChipLimit();
        this.isMeasuringWorkflowChips = false;
      });
    });
  }

  private calculateWorkflowChipLimit() {
    if (!this.workflowChipsContainerRef?.nativeElement) {
      return;
    }
    const container = this.workflowChipsContainerRef.nativeElement;
    const chips = container.querySelectorAll('.chip-with-tooltip');
    if (chips.length === 0) {
      return;
    }

    const rows: number[][] = [];
    let currentRowTop = -1;
    chips.forEach((chip: HTMLElement, index: number) => {
      const top = chip.offsetTop;
      if (top !== currentRowTop) {
        rows.push([]);
        currentRowTop = top;
      }
      rows[rows.length - 1].push(index);
    });

    if (rows.length <= 3) {
      this.workflowChipLimit = this.workflowConfigs.length;
      this.visibleWorkflowConfigs = [...this.workflowConfigs];
      return;
    }

    const chipsInThreeRows = rows[0].length + rows[1].length + rows[2].length;
    this.workflowChipLimit = chipsInThreeRows - 1;
    const visible = this.workflowConfigs.slice(0, this.workflowChipLimit);
    if (this.activeWorkflowConfigId && !visible.some(c => c.id === this.activeWorkflowConfigId)) {
      // Never hide the SELECTED chip under "View More" — e.g. when a deep link
      // selects a workflow that falls past the three-row cutoff.
      this.workflowChipLimit = this.workflowConfigs.length;
      this.visibleWorkflowConfigs = [...this.workflowConfigs];
      return;
    }
    this.visibleWorkflowConfigs = visible;
  }

  toggleShowAllProSearchCategoryChips() {
    this.showAllProSearchCategoryChips = !this.showAllProSearchCategoryChips;
    this.updateVisibleProSearchCategories();
  }

  updateVisibleProSearchCategories() {
    if (this.showAllProSearchCategoryChips) {
      this.visibleProSearchCategoryItems = [...this.proSearchCategoryItems];
      return;
    }
    if (this._proSearchCategoryChipsContainerRef) {
      this.measureProSearchCategoryChips();
    } else {
      this.visibleProSearchCategoryItems = [...this.proSearchCategoryItems];
    }
  }

  private measureProSearchCategoryChips() {
    this.isMeasuringProSearchCategoryChips = true;
    this.visibleProSearchCategoryItems = [...this.proSearchCategoryItems];
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.calculateProSearchCategoryChipLimit();
        this.isMeasuringProSearchCategoryChips = false;
      });
    });
  }

  private calculateProSearchCategoryChipLimit() {
    if (!this._proSearchCategoryChipsContainerRef?.nativeElement) {
      return;
    }
    const container = this._proSearchCategoryChipsContainerRef.nativeElement;
    const chips = container.querySelectorAll('ion-chip');
    if (chips.length === 0) {
      return;
    }

    const rows: number[][] = [];
    let currentRowTop = -1;
    chips.forEach((chip: HTMLElement, index: number) => {
      const top = chip.offsetTop;
      if (top !== currentRowTop) {
        rows.push([]);
        currentRowTop = top;
      }
      rows[rows.length - 1].push(index);
    });

    if (rows.length <= 3) {
      this.proSearchCategoryChipLimit = this.proSearchCategoryItems.length;
      this.visibleProSearchCategoryItems = [...this.proSearchCategoryItems];
      return;
    }

    const chipsInThreeRows = rows[0].length + rows[1].length + rows[2].length;
    this.proSearchCategoryChipLimit = chipsInThreeRows - 1;
    this.visibleProSearchCategoryItems = this.proSearchCategoryItems.slice(0, this.proSearchCategoryChipLimit);
  }

  filterWorkflowChips() {
    const searchText = (this.workflowChipSearchForm.get('search')?.value || '').toLowerCase().trim();
    if (!searchText) {
      this.updateVisibleWorkflowConfigs();
      if (this.isAutoScheduleMode) {
        this.filteredAutoScheduleAddresses = [...this.autoScheduleAddresses];
      }
      return;
    }
    this.visibleWorkflowConfigs = this.workflowConfigs.filter(
      c => c.name.toLowerCase().includes(searchText)
    );
    if (this.isAutoScheduleMode) {
      this.filteredAutoScheduleAddresses = this.autoScheduleAddresses.filter(a => {
        const name = (a.address_name || a.address1 || '').toLowerCase();
        return name.includes(searchText);
      });
    }
  }

  filterProSearchCategoryChips() {
    const searchText = (this.proSearchCategorySearchForm.get('search')?.value || '').toLowerCase().trim();
    if (!searchText) {
      this.updateVisibleProSearchCategories();
      return;
    }
    this.visibleProSearchCategoryItems = this.proSearchCategoryItems.filter(
      item => item.viewValue.toLowerCase().includes(searchText)
    );
  }

  async selectAutoScheduleAddress(address: any) {
    this.selectedAutoScheduleAddress = address;
    localStorage.setItem('unified_agent_auto_schedule_address_id', String(address.id));
    this.autoScheduleLoading = true;
    this.isAutoScheduleFormInitializing = true;
    try {
      // Get job items for this address
      try {
        const jobItems = await this.addressesProvider.getPriceTable(address.id);
        const filteredJobItems = jobItems.filter(job => !job.name.includes('2 Pros'));
        this.autoScheduleJobItems = filteredJobItems.map(job => ({
          viewValue: job.name.replace(/\b\w/g, char => char.toUpperCase()),
          value: job.key
        }));
      } catch {
        this.autoScheduleJobItems = [];
      }
      if (this.autoScheduleJobItems.length === 0) {
        this.autoScheduleJobItems = this.guestReservations.getCleaningOptions();
      }

      this.autoScheduleTimes = this.guestReservations.getAutomaticBookingTimes();

      // Reset job controls
      Object.keys(this.autoScheduleForm.controls).forEach(key => {
        if (key.startsWith('job') && key !== 'job0') {
          this.autoScheduleForm.removeControl(key);
        }
      });
      this.autoScheduleJobControls = ['job0'];
      this.autoScheduleJobsCount = 0;

      const setting = address.address_automatic_booking_setting;
      this.autoScheduleAutomaticBooking = setting?.automatic_booking_enabled || false;

      if (this.autoScheduleAutomaticBooking && setting) {
        this.autoScheduleForm.patchValue({
          startTime: setting.booking_start_time_no_earlier_than,
          endTime: setting.booking_end_time_no_later_than,
          maxDays: setting.max_days_to_perform_job_after_checkout || 0,
        });

        const bookingSettings = await this.guestReservations.getAutomaticBookingSettings(address.id);
        bookingSettings.forEach((bs, index) => {
          if (index === 0) {
            this.autoScheduleForm.patchValue({
              numberDaysPriorToCheckIn: bs.book_when_days_left_to_check_in_date_is_within
            });
            this.autoScheduleForm.get('job0')?.setValue(bs.required_team_service.key);
          } else {
            this.autoScheduleJobsCount++;
            const controlName = `job${this.autoScheduleJobsCount}`;
            this.autoScheduleJobControls.push(controlName);
            this.autoScheduleForm.addControl(controlName, this.fb.control(bs.required_team_service.key, Validators.required));
          }
          this.autoScheduleJobsCount++;
        });
      } else {
        this.autoScheduleForm.patchValue({
          startTime: '10:30',
          endTime: '16:00',
          job0: this.autoScheduleJobItems[2]?.value || this.autoScheduleJobItems[0]?.value,
          numberDaysPriorToCheckIn: 28,
          maxDays: 0,
        });
      }

      this.updateAutoScheduleTimeOptions();
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.isAutoScheduleFormInitializing = false;
      this.autoScheduleLoading = false;

      this.autoScheduleFormSubscription?.unsubscribe();
      this.autoScheduleFormSubscription = this.autoScheduleForm.valueChanges
        .subscribe(() => this.triggerAutoScheduleAutosave());
    }
  }

  updateAutoScheduleTimeOptions() {
    this.checkAutoScheduleTimeRange(
      this.autoScheduleForm.value.startTime,
      this.autoScheduleForm.value.endTime,
      this.autoScheduleForm.value.job0
    );
    this.autoScheduleTimes = this.guestReservations.getAutomaticBookingTimes(
      this.autoScheduleForm.value.startTime,
      this.autoScheduleForm.value.endTime
    );
    this.triggerAutoScheduleAutosave();
  }

  checkAutoScheduleTimeRange(startTime: string, endTime: string, cleaningTime: string) {
    if (!startTime || !endTime || !cleaningTime) {
      this.autoScheduleTimeRangeErrorMessage = '';
      return;
    }
    const defaultLimit = 90;
    const cleaningInMinutes = this.guestReservations.convertStringToMinutes(
      this.guestReservations.getCleaningHours(cleaningTime)
    );
    const startInMinutes = this.guestReservations.convertStringToMinutes(startTime);
    const endInMinutes = this.guestReservations.convertStringToMinutes(endTime);
    const limitTimeWindow = defaultLimit + cleaningInMinutes;
    const timeWindow = endInMinutes - startInMinutes;
    const displayLimit = limitTimeWindow / 60;
    const displayCleaning = cleaningInMinutes / 60;
    if (timeWindow < limitTimeWindow) {
      this.autoScheduleTimeRangeErrorMessage = `To support same day check ins and check outs, we recommend having an "earliest start" and "latest finish" at least ${displayLimit} hours apart for ${displayCleaning} hour jobs.`;
    } else {
      this.autoScheduleTimeRangeErrorMessage = '';
    }
  }

  addAutoScheduleJob() {
    this.autoScheduleJobsCount++;
    const controlName = `job${this.autoScheduleJobsCount}`;
    this.autoScheduleJobControls.push(controlName);
    this.autoScheduleForm.addControl(controlName, this.fb.control('', Validators.required));
    setTimeout(() => {
      let index = this.autoScheduleJobsCount - 1;
      while (this.autoScheduleJobControls.some(
        ctrl => this.autoScheduleForm.get(ctrl)?.value === this.autoScheduleJobItems[index]?.value
      )) {
        index++;
      }
      this.autoScheduleForm.get(controlName)?.setValue(
        this.autoScheduleJobItems[index]?.value || null
      );
    });
  }

  removeAutoScheduleJob(controlName: string) {
    this.autoScheduleJobsCount--;
    this.autoScheduleJobControls = this.autoScheduleJobControls.filter(n => n !== controlName);
    this.autoScheduleForm.removeControl(controlName);
  }

  toggleAutoScheduleBooking() {
    this.autoScheduleAutomaticBooking = !this.autoScheduleAutomaticBooking;
    this.triggerAutoScheduleAutosave();
  }

  triggerAutoScheduleAutosave() {
    if (this.isAutoScheduleFormInitializing) {return;}
    this.updateAutoScheduleSubject.next();
  }

  private async performAutoScheduleSave() {
    if (this.autoScheduleTimeRangeErrorMessage || !this.selectedAutoScheduleAddress) {return;}
    this.isSaving = true;
    try {
      const payload: any = {
        address_ids: [this.selectedAutoScheduleAddress.id],
        booking_start_time_no_earlier_than: this.autoScheduleForm.value.startTime,
        booking_end_time_no_later_than: this.autoScheduleForm.value.endTime,
        automatic_booking_enabled: this.autoScheduleAutomaticBooking,
        max_days_to_perform_job_after_checkout: this.autoScheduleForm.value.maxDays,
        book_when_days_left_to_check_in_date_is_within: this.autoScheduleForm.value.numberDaysPriorToCheckIn
      };

      if (this.autoScheduleJobControls.length === 1) {
        payload.bookable_service_keys = this.autoScheduleForm.value.job0;
      } else {
        payload.bookable_service_keys = this.autoScheduleJobControls
          .map(ctrl => this.autoScheduleForm.get(ctrl)?.value)
          .join(',');
      }

      await this.guestReservations.updateManyAddressAutomaticBooking(payload);
      const addresses = await this.client.getAddresses(true);
      this.autoScheduleAddresses = addresses;
      this.filterAutoScheduleAddresses();
      this.selectedAutoScheduleAddress = addresses.find(
        a => a.id === this.selectedAutoScheduleAddress.id
      );
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      setTimeout(() => this.isSaving = false, 500);
    }
  }

  learnMoreAutoSchedule() {
    this.util.openUrl('https://help.tidy.com/integrations');
  }

  getAddressDisplayName(address: any): string {
    if (address?.address_name) {return address.address_name;}
    return (address?.address1 || '') + (address?.address2 ? `, ${  address.address2}` : '');
  }

  private buildNumberDaysPriorToCheckInItems(): any[] {
    const items = [];
    for (let i = 1; i <= 180; i++) {
      items.push({
        viewValue: `${i} ${i > 1 ? 'days before check in' : 'day before check in'}`,
        value: i
      });
    }
    return items;
  }

  private buildAutoScheduleMaxDaysItems(): any[] {
    return [
      { viewValue: 'The same day as the checkout', value: 0 },
      { viewValue: '1 day after checkout', value: 1 },
      { viewValue: '2 days after checkout', value: 2 },
      { viewValue: '3 days after checkout', value: 3 }
    ];
  }

  // --- Pro Search Methods ---

  async loadProSearchData() {
    this.proSearchLoading = true;
    this.proSearchSearchForm.patchValue({ search: '' });
    this.showAllProSearchChips = false;
    this.proSearchRows = [];
    this.proSearchCurrentRows = [];
    this.proSearchCategoryItems = [];
    this.proSearchCategoryForm.patchValue({ category: '' });
    try {
      const addresses = await this.client.getMoreDetailAddresses();
      this.proSearchAddresses = addresses;
      this.filteredProSearchAddresses = [...addresses];
      this.proSearchAddressSelectItems = addresses.map(a => ({
        viewValue: a.address_name || a.address1,
        value: a.id
      }));
      this.updateVisibleProSearchAddresses();
      if (this.proSearchAddresses.length > 0) {
        this.proSearchAddressSelectForm.patchValue({ address: addresses[0].id }, { emitEvent: false });
        await this.selectProSearchAddress(this.proSearchAddresses[0]);
      }
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.proSearchLoading = false;
    }
  }

  filterProSearchAddresses() {
    const search = (this.proSearchSearchForm.get('search')?.value || '').toLowerCase();
    if (!search) {
      this.filteredProSearchAddresses = [...this.proSearchAddresses];
    } else {
      this.filteredProSearchAddresses = this.proSearchAddresses.filter(a => {
        const name = (a.address_name || a.address1 || '').toLowerCase();
        return name.includes(search);
      });
    }
    this.showAllProSearchChips = false;
    this.updateVisibleProSearchAddresses();
  }

  toggleShowAllProSearchChips() {
    this.showAllProSearchChips = !this.showAllProSearchChips;
    this.updateVisibleProSearchAddresses();
  }

  updateVisibleProSearchAddresses() {
    if (this.showAllProSearchChips) {
      this.visibleProSearchAddresses = [...this.filteredProSearchAddresses];
      return;
    }
    this.visibleProSearchAddresses = [...this.filteredProSearchAddresses];
    this.isMeasuringProSearchChips = true;
    setTimeout(() => {
      this.calculateProSearchChipLimit();
      this.isMeasuringProSearchChips = false;
    });
  }

  private calculateProSearchChipLimit() {
    if (!this.proSearchChipsContainerRef?.nativeElement) {
      return;
    }
    const container = this.proSearchChipsContainerRef.nativeElement;
    const chips = container.querySelectorAll('ion-chip');
    if (chips.length === 0) {
      return;
    }
    const rows: number[][] = [];
    let currentRowTop = -1;
    chips.forEach((chip: HTMLElement, index: number) => {
      const top = chip.offsetTop;
      if (top !== currentRowTop) {
        rows.push([]);
        currentRowTop = top;
      }
      rows[rows.length - 1].push(index);
    });
    if (rows.length <= 3) {
      this.proSearchChipLimit = this.filteredProSearchAddresses.length;
      this.visibleProSearchAddresses = [...this.filteredProSearchAddresses];
      return;
    }
    const chipsInThreeRows = rows[0].length + rows[1].length + rows[2].length;
    this.proSearchChipLimit = chipsInThreeRows - 1;
    this.visibleProSearchAddresses = this.filteredProSearchAddresses.slice(0, this.proSearchChipLimit);
  }

  async selectProSearchAddress(address: any) {
    this.selectedProSearchAddress = address;
    localStorage.setItem('unified_agent_pro_search_address_id', String(address.id));
    this.proSearchLoading = true;
    this.proSearchEditPrio = false;
    try {
      localStorage.setItem('addressId', address.id.toString());
      this.currentAddress.addressId = address.id;

      // Load service categories
      const categories = await this.pros.getServiceCategoriesOrFetch();
      this.proSearchServiceCategoryItems = categories.map(c => ({ viewValue: c.name, value: c.id }));
      this.proSearchCategoryItems = [...this.proSearchServiceCategoryItems];
      this.showAllProSearchCategoryChips = false;

      // Preserve the user's existing category selection across property switches;
      // only fall back to Regular Cleaning on the first load (empty form value).
      const existingCategoryId = parseInt(this.proSearchCategoryForm.get('category')?.value);
      const existingStillAvailable = existingCategoryId
        && this.proSearchCategoryItems.some(item => item.value === existingCategoryId);
      const regularCleaning = this.proSearchCategoryItems.find(
        item => (item.viewValue || '').toLowerCase().includes('regular cleaning')
      );
      const defaultCategoryId = existingStillAvailable
        ? existingCategoryId
        : regularCleaning?.value || this.proSearchCategoryItems[0]?.value || 1;
      this.proSearchCategoryForm.patchValue({ category: defaultCategoryId });

      // Load client settings
      this.proSearchClientSettings = await this.client.getClientSettings();
      this.proSearchSettingsForm.patchValue({
        timeCutoff: this.proSearchClientSettings.reschedule.max_time_cutoff,
        percentCutoff: this.proSearchClientSettings.reschedule.max_percent_cutoff,
      });

      // Load pros for default category
      await this.proSearchGetProsData(defaultCategoryId);
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.proSearchLoading = false;
      this.updateVisibleProSearchCategories();
    }
  }

  async proSearchGetProsData(categoryId?: number) {
    const catId = categoryId || parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
    localStorage.setItem('selectedCategoryId', catId.toString());
    const prosData = await this.pros.getPros(catId, this.selectedProSearchAddress.id);
    this.proSearchProsData = this.pros.parseProsIntoGroups(prosData);
    this.proSearchRowsOutdated = false;
    this.proSearchHasPrivatePro = this.proSearchCheckIfHasPrivatePros();
    this.proSearchHasPros = this.proSearchCheckIfHasPros(catId);
    this.proSearchAllowsFindNewPros = this.proSearchCheckIfAllowsFindNewPros();
    this.proSearchIsAddressOutsideUS = this.proSearchCheckIsAddressOutsideUS();
    this.proSearchPriorityListSentence = this.proSearchGetPriorityListSentence();
  }

  async proSearchChangeCategory(id: number) {
    this.proSearchCategoryForm.patchValue({ category: id });
    this.proSearchLoading = true;
    try {
      await this.proSearchGetProsData(id);
    } catch (error) {
      this.util.showError((error.error?.message) || error.message, 10000);
    } finally {
      this.proSearchLoading = false;
    }
  }

  proSearchGetPriorityListSentence(): string {
    const address = this.selectedProSearchAddress;
    if (!address) {return '';}
    const addressName = address.address_name || address.address1;
    const priorityListSentence = this.pros.updatePriorityListSentence(this.proSearchProsData);
    return `${new TranslationPipe().transform('For <u>') + addressName}</u>, ${priorityListSentence}`;
  }

  proSearchCheckIfHasPros(categoryId: number): boolean {
    const allObjects: any = [];
    this.proSearchProsData.favorited.map((object) => allObjects.push(object));
    this.proSearchProsData.approved.map((object) => allObjects.push(object));
    this.proSearchProsData.blocked.map((object) => allObjects.push(object));
    const isTidyFindNewProsAppearing = allObjects.find((object) => object.object_type === 'dynamic_da');
    let numberOfPros = this.proSearchProsData.favorited.length + this.proSearchProsData.approved.length + this.proSearchProsData.blocked.length;
    if (isTidyFindNewProsAppearing) {
      numberOfPros -= 1;
    }
    return numberOfPros > 0 || categoryId !== 1;
  }

  proSearchCheckIfAllowsFindNewPros(): boolean {
    const isBlocked = this.proSearchProsData.blocked.find((item) => item.object_type === 'dynamic_sa');
    return !isBlocked;
  }

  proSearchCheckIfHasPrivatePros(): boolean {
    const allPros = [...this.proSearchProsData.approved, ...this.proSearchProsData.favorited].flat();
    return allPros.some(pro => pro?.object?.is_private);
  }

  proSearchCheckIsAddressOutsideUS(): boolean {
    const address = this.selectedProSearchAddress;
    return address?.country_code !== 'US';
  }

  async proSearchUpdatePriorities() {
    const save = this.proSearchSavePriorities();
    this.proSearchPrioritiesSave = save.catch(() => null);
    await save;
  }

  private async proSearchSavePriorities() {
    this.isSaving = true;
    try {
      this.proSearchProsData.favorited.map((pro, index) => {
        if (index === 0) {
          if (Array.isArray(pro)) {
            pro.map((item) => { item.priority = 1; });
          } else {
            pro.priority = 1;
          }
        } else {
          const priorPro = this.proSearchProsData.favorited[index - 1];
          const priorProPriority = Array.isArray(priorPro) ? priorPro[0].priority : priorPro.priority;
          if (Array.isArray(pro)) {
            pro.map((item) => { item.priority = priorProPriority + 1; });
          } else {
            pro.priority = priorProPriority + 1;
          }
        }
      });
      const payload = this.proSearchPrepareProsPayload();
      const categoryId = parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
      await this.pros.updateProsPriorities(payload, categoryId);
      this.proSearchRowsOutdated = true;
      this.proSearchProsData.approved = [];
      this.proSearchPriorityListSentence = this.proSearchGetPriorityListSentence();
    } finally {
      setTimeout(() => this.isSaving = false, 500);
    }
  }

  proSearchPrepareProsPayload() {
    const newProsData = JSON.parse(JSON.stringify(this.proSearchProsData));
    newProsData.approved = [];
    newProsData.favorited = [];
    this.proSearchProsData.favorited.forEach((pro) => {
      if (Array.isArray(pro)) {
        pro.forEach((item) => { newProsData.favorited.push({ ...item }); });
      } else {
        newProsData.favorited.push({ ...pro });
      }
    });
    return newProsData;
  }

  proSearchHighlightCards(startIndex: number, endIndex: number) {
    const firstCard = this.proSearchProsData.favorited[startIndex];
    const secondCard = this.proSearchProsData.favorited[endIndex];
    if (firstCard) {firstCard.highlight = true;}
    if (secondCard) {secondCard.highlight = true;}
  }

  proSearchUnhighlightCards(startIndex: number, endIndex: number) {
    const firstCard = this.proSearchProsData.favorited[startIndex];
    const secondCard = this.proSearchProsData.favorited[endIndex];
    if (firstCard) {firstCard.highlight = false;}
    if (secondCard) {secondCard.highlight = false;}
  }

  proSearchMergePros(startIndex: number, endIndex: number) {
    const firstCard = this.proSearchProsData.favorited[startIndex];
    const nextCard = this.proSearchProsData.favorited[endIndex];
    let mergedCards = [];
    if (Array.isArray(firstCard)) {
      mergedCards = [...firstCard];
    } else {
      mergedCards.push(firstCard);
    }
    if (Array.isArray(nextCard)) {
      mergedCards = [...mergedCards, ...nextCard];
    } else {
      mergedCards.push(nextCard);
    }
    this.proSearchProsData.favorited[startIndex] = mergedCards;
    this.proSearchProsData.favorited.splice(endIndex, 1);
    this.proSearchUnhighlightCards(startIndex, endIndex);
    this.proSearchUpdatePriorities();
  }

  proSearchUnmergePro(index: number, proIndex: number) {
    const pros = this.proSearchProsData.favorited[index];
    const pro = pros[proIndex];
    this.proSearchProsData.favorited[index].splice(proIndex, 1);
    this.proSearchProsData.favorited.splice(index + 1, 0, pro);
    const updatedPros = this.proSearchProsData.favorited[index];
    if (updatedPros.length === 1) {
      this.proSearchProsData.favorited[index] = updatedPros[0];
    }
    this.proSearchUnhighlightCards(index, index + 1);
    this.proSearchUpdatePriorities();
  }

  async proSearchRemoveProFromList(pro: any) {
    const params = {
      title: new TranslationPipe().transform('Remove ') + pro?.object?.first_name + new TranslationPipe().transform(' From List?'),
      body: 'The pro will be removed from the list for this address for this service type. You cannot undo this action.',
      backText: 'Go Back',
      confirmText: 'Remove from List',
      confirmAction: this.proSearchConfirmRemoveProFromList.bind(this, pro),
      confirmButtonClass: 'secondary',
      blockAction: this.proSearchConfirmBlockPro.bind(this, pro),
    };
    const modal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await modal.present();
  }

  async proSearchConfirmRemoveProFromList(pro: any) {
    try {
      const categoryId = parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
      const payload = {
        service_type_id: categoryId,
        homekeeper_ids: [pro?.object?.id]
      };
      await this.pros.removeProFromAddressList(this.currentAddress.addressId, payload);
      await this.proSearchGetProsData(categoryId);
      this.modalCtrl.dismiss(true);
      this.util.showSuccess('Pro removed from list');
    } catch (err) {
      this.util.showError((err.error?.message) || err.message, 10000);
    }
  }

  async proSearchConfirmBlockPro(removedPro: any) {
    this.proSearchProsData.favorited.forEach((item, index) => {
      if (Array.isArray(item)) {
        item.forEach((pro, proIndex) => {
          if (pro.id === removedPro.id) {
            this.proSearchProsData.favorited[index].splice(proIndex, 1);
          }
        });
      } else if (item.id === removedPro.id) {
        this.proSearchProsData.favorited.splice(index, 1);
      }
    });
    try {
      this.proSearchProsData.blocked.push(removedPro);
      const categoryId = parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
      const payload = this.proSearchPrepareProsPayload();
      await this.pros.updateProsPriorities(payload, categoryId);
      await this.proSearchGetProsData(categoryId);
      this.modalCtrl.dismiss(true);
      this.util.showSuccess('Pro removed from list');
    } catch (err) {
      this.util.showError((err.error?.message) || err.message, 10000);
    }
  }

  async proSearchUpdateAutoAssign(pro: any) {
    this.isSaving = true;
    const ignoreAvailability = !pro.ignore_homekeeper_availability;
    const addressId = this.selectedProSearchAddress?.id;
    const categoryId = this.proSearchSelectedCategoryId();
    try {
      pro.ignore_homekeeper_availability = ignoreAvailability;
      // A tick made during the order save waits for the reload the first tick starts once
      // that save answers, not only for the save, or it writes to the replaced row.
      let pending: Promise<unknown>;
      do {
        pending = this.proSearchPrioritiesSave;
        await pending;
      } while (pending !== this.proSearchPrioritiesSave);
      if (this.proSearchRowsOutdated && !this.proSearchPageMoved(addressId, categoryId)) {
        // A second click made during this reload waits on it instead of starting its own,
        // which would read the list before this click's write and show the pro as off.
        this.proSearchRowsOutdated = false;
        const reload = this.proSearchGetProsData(categoryId);
        this.proSearchPrioritiesSave = reload.catch(() => { this.proSearchRowsOutdated = true; });
        await reload;
      }
      if (this.proSearchPageMoved(addressId, categoryId)) {
        // The property or category was switched while this tick waited: write to the pro's
        // row on the list the tick was made on, and leave the list now on screen alone.
        const list = this.pros.parseProsIntoGroups(await this.pros.getPros(categoryId, addressId));
        const row = list.favorited.flat()
          .find((r) => r.object_type === pro.object_type && r.object?.id === pro.object?.id);
        if (!row) {
          throw new Error('Could not save auto-assign. Please try again.');
        }
        await this.pros.updateAutoAssignSettings({ ignore_homekeeper_availability: ignoreAvailability }, row.id);
        return;
      }
      const currentRow = this.proSearchFindCurrentRow(pro) ?? pro;
      currentRow.ignore_homekeeper_availability = ignoreAvailability;
      const payload = { ignore_homekeeper_availability: ignoreAvailability };
      await this.pros.updateAutoAssignSettings(payload, currentRow.id);
      this.proSearchPriorityListSentence = this.proSearchGetPriorityListSentence();
    } catch (err) {
      const row = this.proSearchPageMoved(addressId, categoryId) ? pro : this.proSearchFindCurrentRow(pro) ?? pro;
      row.ignore_homekeeper_availability = !ignoreAvailability;
      this.util.showError((err.error?.message) || err.message || 'Could not save auto-assign. Please try again.', 10000);
    } finally {
      setTimeout(() => this.isSaving = false, 500);
    }
  }

  private proSearchSelectedCategoryId(): number {
    return parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
  }

  private proSearchPageMoved(addressId: number, categoryId: number): boolean {
    return this.selectedProSearchAddress?.id !== addressId || this.proSearchSelectedCategoryId() !== categoryId;
  }

  private proSearchFindCurrentRow(pro: any): any {
    return this.proSearchProsData?.favorited
      .flat()
      .find((row) => row.object_type === pro.object_type && row.object?.id === pro.object?.id);
  }

  proSearchGoToPro(proId: number) {
    localStorage.setItem('myProBackPage', 'agent');
    const categoryId = parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
    const params = { categoryId, proId };
    this.rightSidePanelService.navigateTo(`my-pro/${proId}`, params, MyProPage);
  }

  proSearchAddPro() {
    const categoryId = parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
    const params = { serviceCategoryItems: this.proSearchServiceCategoryItems, categoryId };
    this.rightSidePanelService.navigateTo('add-pro', params, AddProPage);
    this.rightSidePanelService.afterPanelClosed().pipe(take(1)).subscribe(async () => {
      await this.proSearchGetProsData(categoryId);
    });
  }

  proSearchGoToFindNewPro(allowsFindNewPros: boolean) {
    const categoryId = parseInt(this.proSearchCategoryForm.get('category')?.value) || 1;
    const params = {
      allowsFindNewPros,
      prosData: this.proSearchProsData,
      hasPrivatePro: this.proSearchHasPrivatePro,
      categoryId
    };
    this.rightSidePanelService.navigateTo('tidy-find-new-pro', params, TidyFindNewProPage);
    this.rightSidePanelService.afterPanelClosed().pipe(take(1)).subscribe(async () => {
      await this.proSearchGetProsData(categoryId);
    });
  }

  proSearchIsProSkippedDueToAutoAssign(index: number): boolean {
    if (!this.proSearchProsData?.favorited) {return false;}
    const currentCard = this.proSearchProsData.favorited[index];
    if (this.proSearchCardHasAutoAssignedPro(currentCard)) {return false;}
    for (let i = 0; i < index; i++) {
      if (this.proSearchCardHasAutoAssignedPro(this.proSearchProsData.favorited[i])) {return true;}
    }
    return false;
  }

  proSearchShouldReduceProOpacity(cardIndex: number, proIndex: number): boolean {
    if (!this.proSearchProsData?.favorited) {return false;}
    const currentCard = this.proSearchProsData.favorited[cardIndex];
    if (!currentCard) {return false;}
    if (currentCard?.object_type) {
      if (currentCard.ignore_homekeeper_availability !== true) {
        for (let i = 0; i < cardIndex; i++) {
          if (this.proSearchCardHasAutoAssignedPro(this.proSearchProsData.favorited[i])) {return true;}
        }
      }
      return false;
    }
    if (Array.isArray(currentCard)) {
      const currentPro = currentCard[proIndex];
      if (!currentPro || currentPro.ignore_homekeeper_availability === true) {return false;}
      for (let i = 0; i < cardIndex; i++) {
        if (this.proSearchCardHasAutoAssignedPro(this.proSearchProsData.favorited[i])) {return true;}
      }
      for (let i = 0; i < proIndex; i++) {
        if (currentCard[i]?.ignore_homekeeper_availability === true) {return true;}
      }
    }
    return false;
  }

  proSearchHasAutoAssignProInList(): boolean {
    if (!this.proSearchProsData?.favorited) {return false;}
    return this.proSearchProsData.favorited.some(card => this.proSearchCardHasAutoAssignedPro(card));
  }

  proSearchHasAutoAssignProAboveIndex(index: number): boolean {
    if (!this.proSearchProsData?.favorited || index <= 0) {return false;}
    for (let i = 0; i < index; i++) {
      if (this.proSearchCardHasAutoAssignedPro(this.proSearchProsData.favorited[i])) {return true;}
    }
    return false;
  }

  private proSearchCardHasAutoAssignedPro(card: any): boolean {
    if (!card) {return false;}
    if (Array.isArray(card)) {return card.some(pro => pro?.ignore_homekeeper_availability === true);}
    return card?.ignore_homekeeper_availability === true;
  }

  async proSearchUpdateTimeCutoff(value: number) {
    this.isSaving = true;
    try {
      const payload = {
        reschedule: {
          max_percent_cutoff: this.proSearchClientSettings.reschedule.max_percent_cutoff,
          max_time_cutoff: value
        }
      };
      await this.client.saveClientSettings(payload);
    } catch {
      this.util.showError(SETTINGS_NOT_SAVED_MESSAGE);
    } finally {
      setTimeout(() => this.isSaving = false, 500);
    }
  }

  async proSearchUpdatePercentCutoff(value: number) {
    this.isSaving = true;
    try {
      const payload = {
        reschedule: {
          max_percent_cutoff: value,
          max_time_cutoff: this.proSearchClientSettings.reschedule.max_time_cutoff
        }
      };
      await this.client.saveClientSettings(payload);
    } catch {
      this.util.showError(SETTINGS_NOT_SAVED_MESSAGE);
    } finally {
      setTimeout(() => this.isSaving = false, 500);
    }
  }

  private buildProSearchTimeCutoffItems(): any[] {
    const items = [];
    const translationPipe = new TranslationPipe();
    for (let i = 1; i <= 72; i++) {
      items.push({
        value: i,
        viewValue: `${i} ${i === 1 ? translationPipe.transform('hour') : translationPipe.transform('hours')}`
      });
    }
    return items;
  }

  private buildProSearchPercentCutoffItems(): any[] {
    const items = [];
    for (let i = 1; i <= 100; i++) {
      items.push({ value: i, viewValue: `${i}%` });
    }
    return items;
  }
}
